import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { formatDateTimeBR, toBrazilDateTimeLocal } from '../../utils/dateBrasilia';
import { isTrackingLink } from '../../utils/tracking';
import { giftRidesSalesInvoice } from '../invoices/orderGiftInvoice';
import {
  ArrowLeft,
  FileText,
  Package,
  Box,
  DollarSign,
  CreditCard,
  Scissors,
  Undo2,
  Copy,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Save,
  X,
  Search,
  AlertTriangle,
  Info,
  Send,
  Truck,
  RefreshCw,
  Tag,
  ClipboardCheck,
  CheckCircle,
  History,
  Calendar,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { ConfirmDialog } from '@components/Dialog';
import { MarkDeliveredModal } from '@components/MarkDeliveredModal';
import { Tabs, TabItem } from '@components/Tabs';
import { CustomerSearchModal } from '@components/CustomerSearchModal';
import { CheckoutModal } from '@components/CheckoutModal';
import { DateInputBR } from '@components/DateInputBR';
import { ProductProjectPhotoThumb } from '@components/ProductProjectPhotoThumb';
import { isoToBR } from '@components/dateInputUtils';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { usePermissions } from '@/hooks/usePermissions';
import { salesOrderService, DeliveryDetails } from '../../services/salesOrderService';
import { UpdateOrderCustomerPayload } from '../../services/salesOrderService';
import { OrderHistoryTab } from './OrderHistoryTab';
import { AddressReviewModal } from './AddressReviewModal';
import { AddressReviewStatus, OrderAddressReviewView, addressReviewStatusLabel } from '../../types/addressReview';
import { normalizeCep, shippingAddressError } from './salesOrderAddress';
import { AddressCepNotice } from '@components/AddressCepNotice';
import { useAddressCepCheck } from '../../hooks/useAddressCepCheck';
import { applyCepToDivergent } from '../../utils/addressCepCheck';
import { productService } from '../../services/productService';
import { Product } from '../../types/product';
import {
  SalesOrder,
  SalesOrderItem,
  SalesOrderVolume,
  SalesOrderInstallment,
  ReplacementOrderItem,
  ReplacementOrderItemPayload,
  SALES_ORDER_MANUAL_STATUS_OPTIONS,
  toManualStatusOptions,
  OrderStatusId,
  getStatusOptionById,
  VolumePayload,
  ItemPayload,
  InstallmentPayload,
  ShippingAddressPayload,
  OrderPaymentMethod,
  OrderFreightQuote,
  OrderStatusOption,
  isUnpaidSaleOrder,
  isRevivableDesmembradoParent,
  canEditDesmembradoParentItems,
  UNPAID_SALE_BLOCKED_STATUS_IDS,
  UNPAID_SALE_STATUS_BLOCK_MSG,
  UNPAID_SALE_INVOICE_BLOCK_MSG,
  isStatusEditableWithInvoice,
  canCancelDueToReturnedInvoice,
  canChangeStatusOfBlockedOrder,
  canChangeCarrierOfBlockedOrder,
} from '../../types/salesOrder';
import { PendingOrdersModal } from '@components/PendingOrdersModal';
import { SplitOrderModal } from './SplitOrderModal';
import { orderTypeService } from '../../services/orderTypeService';
import { OrderType } from '../../types/orderType';
import { salesChannelService } from '../../services/salesChannelService';
import { SalesChannel } from '../../types/salesChannel';
import { PixReceiptCard } from './PixReceiptCard';
import { ChangeOrderCompanyModal } from './ChangeOrderCompanyModal';
import { CardRefundModal } from './CardRefundModal';
import { CardRefundsCard } from './CardRefundsCard';
import { hasHubPaymentRef, latestApprovedRefund } from './cardRefund';
import type { CardRefund } from '../../types/cardRefund';
import { formatFreightQuoteCost, formatFreightQuoteDays } from '@/utils/freightQuoteDisplay';
import { BLOCKED_OPTION_BADGE, isBlockedQuote, quoteBlockReason } from '@/utils/carrierBlocks';
import { freightCarrierDisplay, isQuoteChosenByCustomer } from './chosenCarrier';
import { OrderDeliveryTrackingCard } from './OrderDeliveryTrackingCard';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (v?: number) =>
  v !== undefined && v !== null
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR');
};

// formatDateTimeBR / toBrazilDateTimeLocal — movidos para ../../utils/dateBrasilia (testáveis).

const toDateTimeLocalValue = (dateStr?: string | null) => {
  if (!dateStr) return '';

  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const formatPercent = (v?: number) =>
  v !== undefined && v !== null ? `${v.toFixed(2)}%` : '—';

const formatDocument = (doc?: string) => {
  if (!doc) return '—';
  const d = doc.replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc;
};

const getStatusBadge = (statusId?: number) => {
  if (!statusId) return <span className="text-slate-500">—</span>;
  const opt = getStatusOptionById(statusId);
  if (!opt) return <span className="text-xs text-slate-600">Status {statusId}</span>;
  const colors: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-emerald-100 text-emerald-700',
    teal: 'bg-teal-100 text-teal-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-amber-100 text-amber-700',
    purple: 'bg-purple-100 text-purple-700',
    slate: 'bg-slate-100 text-slate-700',
    orange: 'bg-orange-100 text-orange-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    pink: 'bg-pink-100 text-pink-700',
    indigo: 'bg-indigo-100 text-indigo-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[opt.color] || 'bg-slate-100 text-slate-700'}`}>
      {opt.label}
    </span>
  );
};

// ─── Field display ───────────────────────────────────────────────

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs text-slate-500 uppercase tracking-wider">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
  </div>
);

// ─── Component ───────────────────────────────────────────────────

export const SalesOrderDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { checkPermission, hasRole } = usePermissions();

  const [order, setOrder] = useState<SalesOrder | null>(null);
  // Estorno de cartão: só admin abre o modal; a lista de estornos é de quem lê o pedido.
  const [cardRefundOpen, setCardRefundOpen] = useState(false);
  const [cardRefundsRefresh, setCardRefundsRefresh] = useState(0);
  const [cardRefunds, setCardRefunds] = useState<CardRefund[]>([]);
  const [refundPdfLoading, setRefundPdfLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  // Falha ao carregar o pedido: a tela inteira vira "Pedido não encontrado".
  const [loadError, setLoadError] = useState<string | null>(null);
  // Falha de uma ação sobre o pedido já carregado (salvar volume, item,
  // endereço...): vai para o modal e a tela continua no lugar. Guarda o valor
  // bruto do `catch` para o modal poder listar os campos de um `ApiError`.
  const [actionError, setActionError] = useState<unknown>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [labelLoading, setLabelLoading] = useState(false);

  // Order types
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
  const [orderTypeSaving, setOrderTypeSaving] = useState(false);

  // Sales channels
  const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
  const [salesChannelSaving, setSalesChannelSaving] = useState(false);

  // Clone
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloning, _setCloning] = useState(false);

  // Split
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);

  // Reverse stock (estorno de estoque)
  const [reverseStockOpen, setReverseStockOpen] = useState(false);
  const [reversingStock, setReversingStock] = useState(false);

  // Marcar como entregue (manual, finance/admin)
  const [markDeliveredOpen, setMarkDeliveredOpen] = useState(false);

  // Status
  const [statusChange, setStatusChange] = useState<number | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);
  const [manualStatusOpts, setManualStatusOpts] = useState(() =>
    toManualStatusOptions(SALES_ORDER_MANUAL_STATUS_OPTIONS.map(s => ({
      order_status_id: s.id,
      status_name: s.value,
      can_user_choose_manually: true,
      block_order_change: false,
      allocate_available_stock: false,
    }))),
  );

  // Checkout (conferência)
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  // Análise de endereço (antifraude) do pedido de 1ª compra: trava a conferência e a NF de venda.
  const [addressReview, setAddressReview] = useState<OrderAddressReviewView | null>(null);
  const [addressReviewOpen, setAddressReviewOpen] = useState(false);

  // Delivery details modal
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetails | null>(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryPhotoUrls, setDeliveryPhotoUrls] = useState<{ photo1?: string; photo2?: string }>({});

  // ─── Observations inline edit state ──────────────────────────
  const [obsEditing, setObsEditing] = useState(false);
  const [obsTempValue, setObsTempValue] = useState('');
  const [obsSaving, setObsSaving] = useState(false);
  const [obsError, setObsError] = useState<string | null>(null);
  const [intObsEditing, setIntObsEditing] = useState(false);
  const [intObsTempValue, setIntObsTempValue] = useState('');
  const [intObsSaving, setIntObsSaving] = useState(false);
  const [intObsError, setIntObsError] = useState<string | null>(null);

  // ─── Priority inline edit state ───────────────────────────────
  const [priorityEditing, setPriorityEditing] = useState(false);
  const [priorityTempValue, setPriorityTempValue] = useState(false);
  const [priorityTempTime, setPriorityTempTime] = useState('');
  // Envio agendado (YYYY-MM-DD): antes da data o pedido não entra em rota nem gera envio.
  const [priorityTempShipDate, setPriorityTempShipDate] = useState('');
  const [prioritySaving, setPrioritySaving] = useState(false);
  const [priorityError, setPriorityError] = useState<string | null>(null);

  // ─── Shipping price inline edit state ────────────────────────
  const [shippingPriceEditing, setShippingPriceEditing] = useState(false);
  const [shippingPriceTempValue, setShippingPriceTempValue] = useState<number>(0);
  const [shippingPriceSaving, setShippingPriceSaving] = useState(false);
  const [shippingPriceError, setShippingPriceError] = useState<string | null>(null);

  // ─── Freight discount inline edit state ──────────────────────
  const [freightDiscountEditing, setFreightDiscountEditing] = useState(false);
  const [freightDiscountTempValue, setFreightDiscountTempValue] = useState<number>(0);
  const [freightDiscountSaving, setFreightDiscountSaving] = useState(false);
  const [freightDiscountError, setFreightDiscountError] = useState<string | null>(null);

  // ─── Order discount inline edit state ────────────────────────
  const [discountEditing, setDiscountEditing] = useState(false);
  const [discountTempValue, setDiscountTempValue] = useState<number>(0);
  const [discountSaving, setDiscountSaving] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // ─── Other Expenses inline edit state ────────────────────────
  const [otherExpensesEditing, setOtherExpensesEditing] = useState(false);
  const [otherExpensesTempValue, setOtherExpensesTempValue] = useState<number>(0);
  const [otherExpensesSaving, setOtherExpensesSaving] = useState(false);
  const [otherExpensesError, setOtherExpensesError] = useState<string | null>(null);

  // ─── Recalculate order state ────────────────────────────────
  const [recalculating, setRecalculating] = useState(false);

  // ─── Payment state (IsPayed/PaymentDate) ─────────────────────
  const [paymentEditing, setPaymentEditing] = useState(false);
  const [paymentTempValue, setPaymentTempValue] = useState(false);
  const [paymentDateTempValue, setPaymentDateTempValue] = useState('');
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [followupEditing, setFollowupEditing] = useState(false);
  const [followupTempValue, setFollowupTempValue] = useState(false);
  const [followupDateTempValue, setFollowupDateTempValue] = useState('');
  const [followupSaving, setFollowupSaving] = useState(false);
  const [followupError, setFollowupError] = useState<string | null>(null);

  // ─── Replacement items state ──────────────────────────────────
  const [replacementItems, setReplacementItems] = useState<ReplacementOrderItem[]>([]);
  const [replacementLoading, setReplacementLoading] = useState(false);
  const [replacementModalOpen, setReplacementModalOpen] = useState(false);
  const [customerOrders, setCustomerOrders] = useState<SalesOrder[]>([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const [expandedOrderItems, setExpandedOrderItems] = useState<SalesOrderItem[]>([]);
  const [expandedOrderItemsLoading, setExpandedOrderItemsLoading] = useState(false);
  const [expandedOrderData, setExpandedOrderData] = useState<{ order_discount?: number; order_products_total?: number } | null>(null);
  const [selectedReplacements, setSelectedReplacements] = useState<Record<string, number>>({});
  const [replacementSaving, setReplacementSaving] = useState(false);
  const [replacementError, setReplacementError] = useState<string | null>(null);

  const canViewFinancials = checkPermission('orders.read').hasPermission &&
    (checkPermission('orders.view_financials').hasPermission ||
     checkPermission('*').hasPermission);
  const canViewHistory = checkPermission('orders.view_history').hasPermission ||
    checkPermission('*').hasPermission;
  const canWrite = checkPermission('orders.write').hasPermission || checkPermission('*').hasPermission;
  const canMarkOrderAsPaid = checkPermission('orders.mark_paid').hasPermission || checkPermission('*').hasPermission;
  const canEditFollowup = checkPermission('orders.edit_followup').hasPermission || checkPermission('*').hasPermission;
  const canMarkDelivered = checkPermission('orders.mark_delivered').hasPermission || checkPermission('*').hasPermission;
  const canManageVolumes = checkPermission('orders.manage_volumes').hasPermission || checkPermission('*').hasPermission;
  const canChangeCustomer = checkPermission('orders.customerdatachange').hasPermission || checkPermission('*').hasPermission;
  const canEditDiscount = checkPermission('orders.edit_discount').hasPermission || checkPermission('*').hasPermission;
  const canEditFreightDiscount = checkPermission('orders.edit_freight_discount').hasPermission || checkPermission('*').hasPermission;
  const canEditOtherExpenses = checkPermission('orders.view_financials').hasPermission || checkPermission('*').hasPermission;
  const canEditPaidItems = checkPermission('orders.edit_paid_items').hasPermission || checkPermission('*').hasPermission;

  // Admin and Finance can override BlockOrderChange to change order status
  const canOverrideBlockOrderChange = checkPermission('orders.mark_paid').hasPermission || checkPermission('*').hasPermission;
  // Exceções estreitas à mesma trava (operador logístico), só em pedido sem NF: status em
  // Atendido/Enviado/Entregue e cotação de frete (transportadora) em Atendido.
  const canChangeBlockedStatus = checkPermission('orders.change_status_blocked').hasPermission || checkPermission('*').hasPermission;
  const canChangeCarrierBlocked = checkPermission('orders.change_carrier_blocked').hasPermission || checkPermission('*').hasPermission;

  // ─── Customer search modal state ──────────────────────────────
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerChangeSaving, setCustomerChangeSaving] = useState(false);

  // ─── Volume CRUD state ────────────────────────────────────────
  const [volumeEditId, setVolumeEditId] = useState<number | null>(null);
  const [volumeShowNew, setVolumeShowNew] = useState(false);
  const [volumeForm, setVolumeForm] = useState<Partial<VolumePayload>>({});
  const [volumeSaving, setVolumeSaving] = useState(false);
  const [volumeDeleteId, setVolumeDeleteId] = useState<number | null>(null);
  const [unsavedVolumeDialogOpen, setUnsavedVolumeDialogOpen] = useState(false);
  const pendingUnsavedVolumeActionRef = useRef<(() => void) | null>(null);

  // ─── Volume item assignment state (atribuição opcional de itens a volumes) ──
  const [volumeItemsOpenId, setVolumeItemsOpenId] = useState<number | null>(null);
  const [volumeItemsForm, setVolumeItemsForm] = useState<Record<number, { quantity: string; declared_value: string }>>({});
  const [volumeItemsSaving, setVolumeItemsSaving] = useState(false);
  const [volumeItemsError, setVolumeItemsError] = useState<string | null>(null);

  // ─── Freight Quote state ──────────────────────────────────────
  const [freightQuotes, setFreightQuotes] = useState<OrderFreightQuote[]>([]);
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightCalculating, setFreightCalculating] = useState(false);
  const [freightError, setFreightError] = useState<string | null>(null);
  const [freightSelectSaving, setFreightSelectSaving] = useState(false);

  // ─── Item CRUD state (inline) ─────────────────────────────────
  const [itemEditId, setItemEditId] = useState<number | null>(null);
  const [itemShowNew, setItemShowNew] = useState(false);
  const [itemForm, setItemForm] = useState<Partial<ItemPayload>>({});
  const [itemSaving, setItemSaving] = useState(false);
  const [itemDeleteId, setItemDeleteId] = useState<number | null>(null);
  // Product search for item rows
  const [itemProductQuery, setItemProductQuery] = useState('');
  const [itemProductResults, setItemProductResults] = useState<Product[]>([]);
  const [itemProductLoading, setItemProductLoading] = useState(false);
  const [itemProductOpen, setItemProductOpen] = useState(false);
  const itemProductDebounce = useRef<ReturnType<typeof setTimeout>>();
  // Stock available for the currently selected product in item form
  const [itemSelectedStockAvailable, setItemSelectedStockAvailable] = useState<number | null>(null);

  // Stock data cache for items (keyed by SKU) + pending orders modal
  const [itemStockCache, setItemStockCache] = useState<Record<string, { product_id: number; stock_quantity: number | null; stock_quantity_available: number | null; min_stock_quantity: number | null }>>({});
  const [pendingModal, setPendingModal] = useState<{ open: boolean; productId: number | null; sku: string; description?: string }>({ open: false, productId: null, sku: '' });

  // ─── Installment CRUD state (inline) ─────────────────────────
  const [installmentEditId, setInstallmentEditId] = useState<number | null>(null);
  const [installmentShowNew, setInstallmentShowNew] = useState(false);
  const [installmentForm, setInstallmentForm] = useState<Partial<InstallmentPayload>>({});
  const [installmentSaving, setInstallmentSaving] = useState(false);
  const [installmentDeleteId, setInstallmentDeleteId] = useState<number | null>(null);

  // ─── Payment methods ──────────────────────────────────────────
  const [paymentMethods, setPaymentMethods] = useState<OrderPaymentMethod[]>([]);

  // ─── Confirmação do recálculo de parcelas ─────────────────────
  // Alterar desconto, frete, outras despesas ou itens muda o valor total do pedido. Se o
  // pedido já tem parcelas, elas deixam de somar o total e a emissão da NF passa a falhar
  // ("a soma das parcelas difere do total da nota") — só que lá na frente, para a operação
  // logística. Por isso a alteração é confirmada aqui e o recálculo vai junto, no mesmo passo.
  const [installmentRecalcAction, setInstallmentRecalcAction] = useState<{ run: (confirmed: boolean) => Promise<void> } | null>(null);
  const [installmentRecalcRunning, setInstallmentRecalcRunning] = useState(false);
  const [installmentRecalcBlocked, setInstallmentRecalcBlocked] = useState(false);

  // ─── Address edit state ───────────────────────────────────────
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState<Partial<ShippingAddressPayload>>({});
  const [addressSaving, setAddressSaving] = useState(false);
  const [cepDivergenceConfirmOpen, setCepDivergenceConfirmOpen] = useState(false);

  // ─── All statuses (for block_order_change check) ─────────────
  const [allStatuses, setAllStatuses] = useState<OrderStatusOption[]>([]);

  useEffect(() => {
    salesOrderService.getPaymentMethods().then(setPaymentMethods).catch(() => {});
    salesOrderService.getOrderStatuses().then(data => {
      setAllStatuses(data);
      const opts = toManualStatusOptions(data);
      if (opts.length > 0) setManualStatusOpts(opts);
    }).catch(() => {});
    orderTypeService.listAll().then(setOrderTypes).catch(() => {});
    salesChannelService.listAll().then(setSalesChannels).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const data = await salesOrderService.getOrderById(parseInt(id));
        setOrder(data);
        // Load replacement items
        setReplacementLoading(true);
        try {
          const repItems = await salesOrderService.getReplacementItems(parseInt(id));
          setReplacementItems(repItems);
        } catch { /* ignore — endpoint may not exist yet */ }
        finally { setReplacementLoading(false); }
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Erro ao carregar pedido');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Recarrega a análise de endereço quando o pedido muda de endereço ou de status (a aprovação
  // só vale para o endereço analisado). Falha aqui não impede a tela: o backend é a trava real.
  const addressReviewKey = order
    ? [order.order_id, order.ship_addr_street, order.ship_addr_num, order.ship_addr_district,
      order.ship_addr_city, order.ship_addr_state, order.ship_addr_zip, order.order_status_id].join('|')
    : '';
  useEffect(() => {
    if (!order?.order_id) return;
    let cancelled = false;
    salesOrderService.getAddressReview(order.order_id)
      .then(v => { if (!cancelled) setAddressReview(v); })
      .catch(() => { if (!cancelled) setAddressReview(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressReviewKey]);
  const addressReviewBlocked = !!addressReview?.blocking;
  const addressReviewBlockMsg = addressReview?.block_reason
    || 'Pedido de 1ª compra aguardando a análise do endereço (antifraude).';
  const canReviewAddress = checkPermission('orders.address_review').hasPermission || checkPermission('*').hasPermission;
  const showAddressReviewBtn = !!addressReview && (!!addressReview.review || canReviewAddress);

  const hasUnsavedNewVolumeData = volumeShowNew && [
    Number(volumeForm.height ?? 0),
    Number(volumeForm.width ?? 0),
    Number(volumeForm.length ?? 0),
    Number(volumeForm.diameter ?? 0),
    Number(volumeForm.weight ?? 0),
  ].some(v => v > 0);

  const runWithUnsavedVolumeGuard = (action: () => void) => {
    if (hasUnsavedNewVolumeData) {
      pendingUnsavedVolumeActionRef.current = action;
      setUnsavedVolumeDialogOpen(true);
      return;
    }
    action();
  };

  const cancelUnsavedVolumeDialog = () => {
    setUnsavedVolumeDialogOpen(false);
    pendingUnsavedVolumeActionRef.current = null;
  };

  const confirmDiscardUnsavedVolume = () => {
    setUnsavedVolumeDialogOpen(false);
    cancelVolumeEdit();
    const pendingAction = pendingUnsavedVolumeActionRef.current;
    pendingUnsavedVolumeActionRef.current = null;
    pendingAction?.();
  };

  const handleGuardedInternalLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!hasUnsavedNewVolumeData) return;
    const href = e.currentTarget.getAttribute('href');
    if (!href) return;
    e.preventDefault();
    runWithUnsavedVolumeGuard(() => navigate(href));
  };

  useEffect(() => {
    if (!hasUnsavedNewVolumeData) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedNewVolumeData]);

  // Fetch stock data for order items
  useEffect(() => {
    if (!order?.items?.length) return;
    const skus = order.items.map(i => i.sku).filter(Boolean);
    if (skus.length === 0) return;
    productService.getStockBySku(skus).then(data => {
      setItemStockCache(prev => ({ ...prev, ...data }));
    }).catch(() => {});
  }, [order?.items]);

  // Fetch freight quotes when order loads
  useEffect(() => {
    if (!order?.order_id) return;
    loadFreightQuotes(order.order_id);
  }, [order?.order_id]);

  const loadFreightQuotes = async (orderId: number) => {
    try {
      setFreightLoading(true);
      setFreightError(null);
      const quotes = await salesOrderService.getFreightQuotes(orderId);
      console.log(`📦 loadFreightQuotes(${orderId}): loaded ${quotes.length} quotes`, quotes.map(q => ({ id: q.order_freight_quote_id, provider_id: q.provider_id, is_selected: q.is_selected })));
      setFreightQuotes(quotes);
    } catch (err) {
      console.log(`❌ loadFreightQuotes(${orderId}): ERROR`, err);
      setFreightError(err instanceof Error ? err.message : 'Erro ao carregar cotações de frete');
    } finally {
      setFreightLoading(false);
    }
  };

  const handleCalculateFreight = async () => {
    if (!order) return;
    try {
      setFreightCalculating(true);
      setFreightError(null);
      const quotes = await salesOrderService.calculateFreightQuotes(order.order_id);
      setFreightQuotes(quotes);
    } catch (err) {
      setFreightError(err instanceof Error ? err.message : 'Erro ao calcular cotações de frete');
    } finally {
      setFreightCalculating(false);
    }
  };

  const handleSelectFreightQuote = async (quoteId: number) => {
    if (!order) return;
    try {
      setFreightSelectSaving(true);
      const quotes = await salesOrderService.selectFreightQuote(order.order_id, {
        order_freight_quote_id: quoteId,
        is_selected: true,
      });
      setFreightQuotes(quotes);
    } catch (err) {
      setFreightError(err instanceof Error ? err.message : 'Erro ao selecionar cotação de frete');
    } finally {
      setFreightSelectSaving(false);
    }
  };

  /**
   * Troca a empresa emissora do pedido. Recarrega o pedido com a resposta porque o backend
   * recalcula os totais (VariableFees é por empresa).
   */
  const handleChangeCompany = async (companyId: number) => {
    if (!order) return;
    try {
      setCompanySaving(true);
      setCompanyError(null);
      const updated = await salesOrderService.changeOrderCompany(order.order_id, companyId);
      setOrder(updated);
      setCompanyModalOpen(false);
    } catch (err) {
      setCompanyError(err instanceof Error ? err.message : 'Erro ao alterar a empresa do pedido');
    } finally {
      setCompanySaving(false);
    }
  };

  // Estornos de cartão do pedido (quem lê o pedido): alimentam o bloco da lista e
  // o botão do cabeçalho, que vira "Baixar comprovante de estorno" depois do estorno.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    salesOrderService
      .listCardRefunds(parseInt(id))
      .then((list) => {
        if (!cancelled) setCardRefunds(list);
      })
      .catch(() => {
        if (!cancelled) setCardRefunds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, cardRefundsRefresh]);

  const handleDownloadRefundReceipt = async (refundId: number) => {
    if (!id) return;
    try {
      setRefundPdfLoading(true);
      setPdfError(null);
      await salesOrderService.downloadCardRefundPdf(parseInt(id), refundId);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Falha ao gerar o comprovante de estorno');
    } finally {
      setRefundPdfLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!id) return;
    try {
      setPdfLoading(true);
      setPdfError(null);
      await salesOrderService.downloadPdf(parseInt(id));
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Falha ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadShippingLabel = async () => {
    if (!id) return;
    try {
      setLabelLoading(true);
      setPdfError(null);
      await salesOrderService.downloadShippingLabel(parseInt(id));
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Erro ao gerar etiqueta de envio');
    } finally {
      setLabelLoading(false);
    }
  };

  const handleStartCheckout = async () => {
    if (!id || !order) return;
    try {
      // Set status to "Em Conferência" when starting checkout
      if (order.order_status_id !== OrderStatusId.EmConferencia) {
        const updated = await salesOrderService.updateOrderStatus(parseInt(id), OrderStatusId.EmConferencia);
        setOrder(prev => prev ? { ...prev, ...updated } : updated);
      }
      setCheckoutOpen(true);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Erro ao iniciar conferência');
    }
  };

  const handleCheckoutComplete = async () => {
    if (!id) return;
    // Set status to "Conferido" when checkout is complete
    const updated = await salesOrderService.updateOrderStatus(parseInt(id), OrderStatusId.Conferido);
    setOrder(prev => prev ? { ...prev, ...updated } : updated);
    setCheckoutOpen(false);
  };

  const [deliveryError, setDeliveryError] = useState<string | null>(null);

  const openDeliveryModal = async () => {
    if (!order) return;
    setDeliveryModalOpen(true);
    setDeliveryLoading(true);
    setDeliveryError(null);
    setDeliveryDetails(null);
    setDeliveryPhotoUrls({});
    try {
      const details = await salesOrderService.getDeliveryDetails(order.order_id);
      setDeliveryDetails(details);
      const [photo1Url, photo2Url] = await Promise.all([
        details.evidence_photo_1_url ? salesOrderService.getDeliveryPhotoUrl(order.order_id, 1) : Promise.resolve(undefined),
        details.evidence_photo_2_url ? salesOrderService.getDeliveryPhotoUrl(order.order_id, 2) : Promise.resolve(undefined),
      ]);
      setDeliveryPhotoUrls({ photo1: photo1Url, photo2: photo2Url });
    } catch (err) {
      setDeliveryError(err instanceof Error ? err.message : 'Erro ao buscar detalhes da entrega');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const handleClone = async () => {
    if (!order) return;
    setCloneOpen(false);
    navigate('/sales-orders/new', { state: { cloneSource: order } });
  };

  const handleSplitConfirm = async (
    order1StatusId: number,
    order2StatusId: number,
    order1Items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[],
    order2Items: { sku: string; quantity: number; unit_price?: number; unit_cost?: number; discount?: number }[],
    replacementItemsTarget: 1 | 2 | null,
  ) => {
    if (!order) return;
    const statusBlocked = (() => {
      if (canOverrideBlockOrderChange) return false;
      const fromApi = allStatuses.find(s => s.order_status_id === order.order_status_id);
      if (fromApi) return fromApi.block_order_change;
      return [
        OrderStatusId.Atendido,
        OrderStatusId.Cancelado,
        OrderStatusId.Enviado,
        OrderStatusId.Entregue,
      ].includes(order.order_status_id ?? 0);
    })();
    if (statusBlocked) {
      setActionError('Este pedido não pode ser desmembrado porque o status atual bloqueia alterações.');
      setSplitOpen(false);
      return;
    }
    try {
      setSplitting(true);
      setSplitError(null);
      await salesOrderService.splitOrder({
        parent_order_id: order.parent_order_id ?? order.order_id,
        source_order_id: order.parent_order_id ? order.order_id : undefined,
        order1_status_id: order1StatusId,
        order2_status_id: order2StatusId,
        order1_items: order1Items,
        order2_items: order2Items,
        replacement_items_target_order: replacementItemsTarget ?? undefined,
        is_resplit: Boolean(order.parent_order_id),
      });
      setSplitOpen(false);
      setSplitError(null);
      // Reload
      const data = await salesOrderService.getOrderById(order.order_id);
      setOrder(data);
    } catch (err) {
      setSplitError(err instanceof Error ? err.message : 'Erro ao desmembrar pedido');
    } finally {
      setSplitting(false);
    }
  };

  const handleReverseStock = async () => {
    if (!order) return;
    try {
      setReversingStock(true);
      setActionError(null);
      const updated = await salesOrderService.reverseStock(order.order_id);
      setReverseStockOpen(false);
      // Reload to reflect new stock_updated flag and any derived fields
      const data = await salesOrderService.getOrderById(order.order_id);
      setOrder(data ?? updated);
    } catch (err) {
      setActionError(err ?? 'Erro ao estornar estoque');
      setReverseStockOpen(false);
    } finally {
      setReversingStock(false);
    }
  };

  const confirmStatusChange = async () => {
    if (!id || !statusChange) return;
    try {
      setChangingStatus(true);
      const updated = await salesOrderService.updateOrderStatus(parseInt(id), statusChange);
      setOrder(prev => prev ? { ...prev, ...updated } : updated);

      // Se cancelando pedido que possui proposta comercial vinculada, cancelar a proposta também
      if (statusChange === OrderStatusId.Cancelado && order?.proposal_num) {
        await salesOrderService.cancelLinkedProposal(parseInt(id)).catch(() => {
          // Não bloqueia — a proposta será cancelada pelo backend automaticamente
        });
      }

      setStatusChange(null);
    } catch (err) {
      setActionError(err ?? 'Erro ao alterar status');
    } finally {
      setChangingStatus(false);
    }
  };

  // ─── Volume handlers ──────────────────────────────────────────

  const startEditVolume = (vol: SalesOrderVolume) => {
    setVolumeEditId(vol.order_volume_id!);
    setVolumeForm({ height: vol.height, width: vol.width, length: vol.length, diameter: vol.diameter, weight: vol.weight });
    setVolumeShowNew(false);
    setVolumeItemsOpenId(null);
    setVolumeItemsError(null);
  };

  const startNewVolume = () => {
    setVolumeShowNew(true);
    setVolumeEditId(null);
    setVolumeForm({ height: 0, width: 0, length: 0, diameter: 0, weight: 0 });
  };

  const cancelVolumeEdit = () => {
    setVolumeEditId(null);
    setVolumeShowNew(false);
    setVolumeForm({});
  };

  const saveVolume = async () => {
    if (!order) return;
    try {
      setVolumeSaving(true);
      const payload = volumeForm as VolumePayload;
      const updated = volumeEditId !== null
        ? await salesOrderService.updateVolume(order.order_id, volumeEditId, payload)
        : await salesOrderService.addVolume(order.order_id, payload);
      setOrder(prev => prev ? { ...prev, ...updated } : updated);
      cancelVolumeEdit();
      // Volume changed — clear freight quotes and recalculate
      try {
        await salesOrderService.deleteFreightQuotes(order.order_id);
        setFreightQuotes([]);
        const quotes = await salesOrderService.calculateFreightQuotes(order.order_id);
        setFreightQuotes(quotes);
      } catch {
        setFreightQuotes([]);
      }
    } catch (err) {
      setActionError(err ?? 'Erro ao salvar volume');
    } finally {
      setVolumeSaving(false);
    }
  };

  const confirmDeleteVolume = async () => {
    if (!order || volumeDeleteId === null) return;
    try {
      const updated = await salesOrderService.deleteVolume(order.order_id, volumeDeleteId);
      setOrder(prev => prev ? { ...prev, ...updated } : updated);
      setVolumeDeleteId(null);
      // Volume changed — clear freight quotes and recalculate
      try {
        await salesOrderService.deleteFreightQuotes(order.order_id);
        setFreightQuotes([]);
        if ((updated.volumes?.length ?? 0) > 0) {
          const quotes = await salesOrderService.calculateFreightQuotes(order.order_id);
          setFreightQuotes(quotes);
        }
      } catch {
        setFreightQuotes([]);
      }
    } catch (err) {
      setActionError(err ?? 'Erro ao excluir volume');
    }
  };

  // ─── Volume item assignment handlers ──────────────────────────
  // A atribuição de itens a volumes é sempre opcional e não bloqueia nenhum fluxo.

  const cancelVolumeItems = () => {
    setVolumeItemsOpenId(null);
    setVolumeItemsForm({});
    setVolumeItemsError(null);
  };

  const toggleVolumeItems = (vol: SalesOrderVolume) => {
    if (volumeItemsOpenId === vol.order_volume_id) {
      cancelVolumeItems();
      return;
    }
    const form: Record<number, { quantity: string; declared_value: string }> = {};
    (vol.items ?? []).forEach(vi => {
      form[vi.order_item_id] = {
        quantity: String(vi.quantity),
        declared_value: vi.declared_value !== undefined && vi.declared_value !== null ? String(vi.declared_value) : '',
      };
    });
    setVolumeItemsForm(form);
    setVolumeItemsOpenId(vol.order_volume_id!);
    setVolumeItemsError(null);
  };

  const setVolumeItemField = (orderItemId: number, field: 'quantity' | 'declared_value', value: string) => {
    setVolumeItemsForm(f => {
      const current = f[orderItemId] ?? { quantity: '', declared_value: '' };
      return { ...f, [orderItemId]: { ...current, [field]: value } };
    });
  };

  /** Quantidade do item já alocada nos DEMAIS volumes do pedido */
  const allocatedInOtherVolumes = (orderItemId: number, volumeId: number): number =>
    (order?.volumes ?? []).reduce(
      (sum, v) =>
        v.order_volume_id === volumeId
          ? sum
          : sum + (v.items ?? []).filter(vi => vi.order_item_id === orderItemId).reduce((s, vi) => s + vi.quantity, 0),
      0,
    );

  const saveVolumeItems = async () => {
    if (!order || volumeItemsOpenId === null) return;
    try {
      setVolumeItemsSaving(true);
      setVolumeItemsError(null);
      const payloadItems = Object.entries(volumeItemsForm)
        .map(([orderItemId, f]) => {
          const quantity = parseInt(f.quantity, 10) || 0;
          const declared = f.declared_value.trim() === '' ? NaN : parseFloat(f.declared_value);
          return {
            order_item_id: Number(orderItemId),
            quantity,
            ...(Number.isFinite(declared) ? { declared_value: declared } : {}),
          };
        })
        .filter(i => i.quantity > 0);
      await salesOrderService.setVolumeItems(order.order_id, volumeItemsOpenId, { items: payloadItems });
      // Recarrega o pedido para refletir as atribuições em todos os volumes
      const data = await salesOrderService.getOrderById(order.order_id);
      setOrder(data);
      cancelVolumeItems();
    } catch (err) {
      setVolumeItemsError(err instanceof Error ? err.message : 'Erro ao salvar itens do volume');
    } finally {
      setVolumeItemsSaving(false);
    }
  };

  // ─── Recálculo de parcelas em alterações de valor ─────────────

  /**
   * Parcelas "reais" do pedido — fora a de crédito de troca/devolução, que espelha o
   * Desc. Troca e é mantida pelo próprio backend. É a mesma classificação que o ERPApi faz
   * em loadInstallmentRecalcLines, para a tela perguntar exatamente quando ele exigiria.
   */
  const realInstallments = useMemo(() => {
    const all = order?.installments ?? [];
    const returnCreditPm = paymentMethods.find(pm => pm.is_product_credit_4_return);
    if (!returnCreditPm) return all;
    return all.filter(i => i.payment_method_id !== returnCreditPm.payment_method_id);
  }, [order?.installments, paymentMethods]);

  /**
   * Valor de venda de uma linha do pedido — espelha domain.OrderItemLineTotal no ERPApi.
   * Um item presente (desconto de 100%) vale zero e portanto não muda o total.
   */
  const itemLineTotal = (unitPrice?: number, discountPercent?: number, quantity?: number) =>
    (unitPrice ?? 0) * (1 - (discountPercent ?? 0) / 100) * (quantity ?? 0);

  /** Diferença acima de meio centavo é mudança real de valor; abaixo é ruído. */
  const totalChanged = (before: number, after: number) => Math.abs(after - before) > 0.005;

  /**
   * Executa uma alteração que pode mudar o valor total do pedido.
   *
   * Sem parcelas — ou quando a alteração não mexe no total —, roda direto. Caso contrário
   * abre a modal de confirmação e só executa com o "sim" do usuário, que também autoriza o
   * recálculo enviado ao backend. Um "não" cancela a alteração inteira: nada é gravado, nem
   * o novo valor nem as parcelas.
   *
   * Quem não pode alterar parcelas (orders.view_financials) não segue adiante: o backend
   * responderia 403, e avisar aqui evita uma tentativa que só falharia depois.
   *
   * `changesTotal` repete de propósito a decisão que o ERPApi toma em ChangesOrderTotal /
   * OrderItemLineTotal — aqui só para não abrir modal à toa; quem manda é o backend.
   */
  const runOrderTotalChange = async (changesTotal: boolean, action: (recalculateInstallments: boolean) => Promise<void>) => {
    if (!changesTotal || realInstallments.length === 0) {
      await action(false);
      return;
    }
    if (!canViewFinancials) {
      setInstallmentRecalcBlocked(true);
      return;
    }
    setInstallmentRecalcAction({ run: action });
  };

  const confirmInstallmentRecalc = async () => {
    if (!installmentRecalcAction) return;
    setInstallmentRecalcRunning(true);
    try {
      await installmentRecalcAction.run(true);
      setInstallmentRecalcAction(null);
    } finally {
      setInstallmentRecalcRunning(false);
    }
  };

  // ─── Item handlers (inline) ───────────────────────────────────

  const startEditItem = (item: SalesOrderItem) => {
    setItemEditId(item.order_item_id!);
    setItemForm({ sku: item.sku, description: item.description, quantity: item.quantity, unit_price: item.unit_price, unit_cost: item.unit_cost, discount: item.discount, is_gift: item.is_gift ?? false });
    setItemShowNew(false);
    setItemProductQuery(item.description || item.sku || '');
    setItemProductOpen(false);
    setItemProductResults([]);
  };

  const startNewItem = () => {
    setItemShowNew(true);
    setItemEditId(null);
    setItemForm({ sku: '', description: '', quantity: 1, unit_price: 0, unit_cost: 0, discount: 0, is_gift: false });
    setItemProductQuery('');
    setItemProductOpen(false);
    setItemProductResults([]);
    setItemSelectedStockAvailable(null);
  };

  const cancelItemEdit = () => {
    setItemEditId(null);
    setItemShowNew(false);
    setItemForm({});
    setItemProductQuery('');
    setItemProductResults([]);
    setItemProductOpen(false);
    setItemSelectedStockAvailable(null);
  };

  const saveItemInline = async () => {
    if (!order) return;
    // Item presente entra com 100% de desconto: a linha vale zero e o total não muda.
    const effectiveDiscount = itemForm.is_gift ? 100 : itemForm.discount;
    const lineAfter = itemLineTotal(itemForm.unit_price, effectiveDiscount, itemForm.quantity);
    const editing = itemEditId !== null ? (order.items ?? []).find(i => i.order_item_id === itemEditId) : undefined;
    const lineBefore = editing ? itemLineTotal(editing.unit_price, editing.discount, editing.quantity) : 0;
    await runOrderTotalChange(totalChanged(lineBefore, lineAfter), async (recalculateInstallments) => {
      try {
        setItemSaving(true);
        const payload = itemForm as ItemPayload;
        const updated = itemEditId !== null
          ? await salesOrderService.updateItem(order.order_id, itemEditId, payload, recalculateInstallments)
          : await salesOrderService.addItem(order.order_id, payload, recalculateInstallments);
        setOrder(prev => prev ? { ...prev, ...updated } : updated);
        cancelItemEdit();
      } catch (err) {
        setActionError(err ?? 'Erro ao salvar item');
      } finally {
        setItemSaving(false);
      }
    });
  };

  const handleItemProductSearch = (query: string) => {
    setItemProductQuery(query);
    setItemForm(f => ({ ...f, sku: '', description: '' }));
    clearTimeout(itemProductDebounce.current);
    if (!query.trim()) {
      setItemProductResults([]);
      setItemProductOpen(false);
      return;
    }
    setItemProductOpen(true);
    setItemProductLoading(true);
    itemProductDebounce.current = setTimeout(async () => {
      try {
        const result = await productService.searchProducts(query.trim(), 1, 10, { active: true });
        setItemProductResults(result.products || []);
      } catch {
        setItemProductResults([]);
      } finally {
        setItemProductLoading(false);
      }
    }, 300);
  };

  const handleItemProductSelect = (product: Product) => {
    setItemForm(f => ({
      ...f,
      sku: product.sku,
      description: product.description,
      unit_price: product.price ?? f.unit_price,
      unit_cost: product.cost ?? f.unit_cost,
    }));
    setItemSelectedStockAvailable(product.stock_quantity_available ?? null);
    setItemProductQuery(product.description);
    setItemProductOpen(false);
    setItemProductResults([]);
  };

  const confirmDeleteItem = async () => {
    if (!order || itemDeleteId === null) return;
    const itemID = itemDeleteId;
    const removed = (order.items ?? []).find(i => i.order_item_id === itemID);
    const removedTotal = itemLineTotal(removed?.unit_price, removed?.discount, removed?.quantity);
    // Fecha a confirmação de exclusão antes de (eventualmente) abrir a de recálculo, para
    // não empilhar duas modais na tela.
    setItemDeleteId(null);
    await runOrderTotalChange(totalChanged(removedTotal, 0), async (recalculateInstallments) => {
      try {
        const updated = await salesOrderService.deleteItem(order.order_id, itemID, recalculateInstallments);
        setOrder(prev => prev ? { ...prev, ...updated } : updated);
      } catch (err) {
        setActionError(err ?? 'Erro ao excluir item');
      }
    });
  };

  // ─── Installment handlers (inline) ───────────────────────────

  const startEditInstallment = (inst: SalesOrderInstallment) => {
    setInstallmentEditId(inst.order_installment_id!);
    setInstallmentForm({ value: inst.value, due_date: inst.due_date, notes: inst.notes, payment_method_id: inst.payment_method_id, payment_method_name: inst.payment_method_name });
    setInstallmentShowNew(false);
  };

  const startNewInstallment = () => {
    setInstallmentShowNew(true);
    setInstallmentEditId(null);
    setInstallmentForm({ value: 0, due_date: new Date().toISOString().slice(0, 10), notes: '', payment_method_id: undefined, payment_method_name: '' });
  };

  const cancelInstallmentEdit = () => {
    setInstallmentEditId(null);
    setInstallmentShowNew(false);
    setInstallmentForm({});
  };

  const saveInstallmentInline = async () => {
    if (!order) return;
    try {
      setInstallmentSaving(true);
      const payload = installmentForm as InstallmentPayload;
      const updated = installmentEditId !== null
        ? await salesOrderService.updateInstallment(order.order_id, installmentEditId, payload)
        : await salesOrderService.addInstallment(order.order_id, payload);
      setOrder(prev => prev ? { ...prev, ...updated } : updated);
      cancelInstallmentEdit();
    } catch (err) {
      setActionError(err ?? 'Erro ao salvar parcela');
    } finally {
      setInstallmentSaving(false);
    }
  };

  const confirmDeleteInstallment = async () => {
    if (!order || installmentDeleteId === null) return;
    try {
      const updated = await salesOrderService.deleteInstallment(order.order_id, installmentDeleteId);
      setOrder(prev => prev ? { ...prev, ...updated } : updated);
      setInstallmentDeleteId(null);
    } catch (err) {
      setActionError(err ?? 'Erro ao excluir parcela');
    }
  };

  // ─── Customer change handler ────────────────────────────────

  const handleCustomerChange = async (customer: import('../../types/salesOrder').Customer) => {
    if (!order) return;
    try {
      setCustomerChangeSaving(true);
      const hasAddressFilled = order.ship_addr_street || order.ship_addr_city || order.ship_addr_zip;
      const addr = customer.default_address;
      const payload: UpdateOrderCustomerPayload = {
        customer_id: customer.customer_id,
        ...(!hasAddressFilled && addr ? {
          ship_addr_street: addr.addr_street,
          ship_addr_num: addr.addr_num,
          ship_addr_compl: addr.addr_compl,
          ship_addr_district: addr.addr_district,
          ship_addr_city: addr.addr_city,
          ship_addr_state: addr.addr_state,
          ship_addr_zip: addr.addr_zip,
        } : {}),
      };
      const updated = await salesOrderService.updateOrderCustomer(order.order_id, payload);
      setOrder(prev => prev ? { ...prev, ...updated } : updated);
      setCustomerSearchOpen(false);
    } catch (err) {
      setActionError(err ?? 'Erro ao alterar cliente');
    } finally {
      setCustomerChangeSaving(false);
    }
  };

  // ─── Address handler ──────────────────────────────────────────

  // Conferência automática do endereço pelo CEP. Em edição confere o formulário; fora dela
  // confere o endereço gravado (pedidos vindos do CRM/site), para a logística ver um CEP de
  // outra cidade antes de cotar frete — caso do pedido 29746 (CEP da Bahia, endereço de SP).
  // Pedido já enviado, entregue ou cancelado não consulta: não há mais o que corrigir.
  const addressCepCheck = useAddressCepCheck(
    editingAddress
      ? {
        zip: addressForm.ship_addr_zip ?? '',
        uf: addressForm.ship_addr_state ?? '',
        city: addressForm.ship_addr_city ?? '',
        district: addressForm.ship_addr_district ?? '',
        street: addressForm.ship_addr_street ?? '',
      }
      : {
        zip: order?.ship_addr_zip ?? '',
        uf: order?.ship_addr_state ?? '',
        city: order?.ship_addr_city ?? '',
        district: order?.ship_addr_district ?? '',
        street: order?.ship_addr_street ?? '',
      },
    {
      enabled: Boolean(order) && (editingAddress || ![
        OrderStatusId.Enviado,
        OrderStatusId.Entregue,
        OrderStatusId.Cancelado,
      ].includes(order?.order_status_id as OrderStatusId)),
      onFound: cep => {
        if (!editingAddress) return;
        setAddressForm(f => ({
          ...f,
          ship_addr_street: (f.ship_addr_street ?? '').trim() ? f.ship_addr_street : cep.street,
          ship_addr_district: (f.ship_addr_district ?? '').trim() ? f.ship_addr_district : cep.district,
          ship_addr_city: (f.ship_addr_city ?? '').trim() ? f.ship_addr_city : cep.city,
          ship_addr_state: (f.ship_addr_state ?? '').trim() ? f.ship_addr_state : cep.uf,
        }));
      },
    },
  );

  const applyCepToAddressForm = () => {
    if (!addressCepCheck.cepAddress) return;
    const next = applyCepToDivergent(
      {
        uf: addressForm.ship_addr_state ?? '',
        city: addressForm.ship_addr_city ?? '',
        district: addressForm.ship_addr_district ?? '',
        street: addressForm.ship_addr_street ?? '',
      },
      addressCepCheck.cepAddress,
      addressCepCheck.divergences,
    );
    setAddressForm(f => ({
      ...f,
      ship_addr_state: next.uf,
      ship_addr_city: next.city,
      ship_addr_district: next.district,
      ship_addr_street: next.street,
    }));
  };

  const startEditAddress = () => {
    setAddressForm({
      ship_addr_street: order?.ship_addr_street,
      ship_addr_num: order?.ship_addr_num,
      ship_addr_compl: order?.ship_addr_compl,
      ship_addr_district: order?.ship_addr_district,
      ship_addr_city: order?.ship_addr_city,
      ship_addr_state: order?.ship_addr_state,
      ship_addr_zip: order?.ship_addr_zip,
    });
    setEditingAddress(true);
  };

  const saveAddress = async (cepDivergenceConfirmed = false) => {
    if (!order) return;
    // Mesma regra do backend, aplicada antes de enviar — o erro chega no campo, não num 400.
    const addrError = shippingAddressError(addressForm);
    if (addrError) { setActionError(addrError); return; }
    // Endereço que não confere com o CEP não bloqueia (a base de CEP pode estar desatualizada),
    // mas exige confirmação explícita.
    if (!cepDivergenceConfirmed && addressCepCheck.divergences.length > 0) {
      setCepDivergenceConfirmOpen(true);
      return;
    }
    try {
      setAddressSaving(true);
      console.log('🏠 saveAddress: order state before save=', { order_type_id: order.order_type_id, observation: order.observation, ship_addr_street: order.ship_addr_street });
      console.log('🏠 saveAddress: addressForm=', addressForm);
      const updated = await salesOrderService.updateShippingAddress(order.order_id, addressForm as ShippingAddressPayload);
      console.log('🏠 saveAddress: response updated=', { order_type_id: updated.order_type_id, observation: updated.observation, ship_addr_street: updated.ship_addr_street });
      setOrder(prev => prev ? { ...prev, ...addressForm, ship_addr_street: updated.ship_addr_street, ship_addr_num: updated.ship_addr_num, ship_addr_compl: updated.ship_addr_compl, ship_addr_district: updated.ship_addr_district, ship_addr_city: updated.ship_addr_city, ship_addr_state: updated.ship_addr_state, ship_addr_zip: updated.ship_addr_zip } : prev);
      setEditingAddress(false);
    } catch (err) {
      setActionError(err ?? 'Erro ao salvar endereço');
    } finally {
      setAddressSaving(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => runWithUnsavedVolumeGuard(() => navigate('/sales-orders'))}><ArrowLeft size={20} /></Button>
          <div className="h-8 bg-slate-200 rounded w-56 animate-pulse" />
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-emerald-600" size={32} />
        </div>
      </div>
    );
  }

  if (loadError || !order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => runWithUnsavedVolumeGuard(() => navigate('/sales-orders'))}><ArrowLeft size={20} /></Button>
          <h1 className="text-2xl font-bold text-slate-900">Pedido não encontrado</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{loadError || 'Pedido não encontrado.'}</div>
      </div>
    );
  }

  const items = order.items || [];
  const volumes = order.volumes || [];
  const installments = order.installments || [];
  const isStatusBlocked = (() => {
    // Admin and finance can override BlockOrderChange
    if (canOverrideBlockOrderChange) return false;
    const fromApi = allStatuses.find(s => s.order_status_id === order.order_status_id);
    if (fromApi) return fromApi.block_order_change;
    return [
      OrderStatusId.Atendido,
      OrderStatusId.Cancelado,
      OrderStatusId.Enviado,
      OrderStatusId.Entregue,
    ].includes(order.order_status_id ?? 0);
  })();
  const isReadonly = isStatusBlocked || !!order.invoice_id;
  // Troca da empresa emissora: espelha decideCompanyChangeAllowed do backend. Só a nota (fiscal
  // ou de bonificação) e o cancelamento barram — de propósito NÃO usa isReadonly, porque um
  // pedido Atendido/Entregue ainda sem NF é justamente onde o erro de empresa aparece.
  const canChangeCompany = !order.invoice_id
    && !order.gift_invoice_id
    && order.order_status_id !== OrderStatusId.Cancelado;
  // Pai desmembrado com todos os filhos cancelados: o desmembramento perdeu o efeito e o backend
  // volta a aceitar a troca de status (domain.AllChildOrdersCancelled). Libera SÓ o status — itens,
  // cliente e endereço continuam bloqueados enquanto o pedido estiver em "Desmembrado".
  const canChangeStatusOfDesmembradoParent = isRevivableDesmembradoParent(order) && !order.invoice_id;
  // Pedido em "Em Aberto"/"Venda Agenciada" com NF emitida: o campo de status continua
  // editável (a logística move para "Em Andamento" depois da NF) — só a NF não trava.
  const statusEditableDespiteInvoice = !isStatusBlocked && isStatusEditableWithInvoice(order.order_status_id);
  // NF do pedido com devolução vinculada (devolução fora do prazo de cancelamento da NF-e): o
  // pedido volta a poder ser CANCELADO, sem desvincular a NF. Ver canCancelDueToReturnedInvoice.
  const cancelWithReturnAllowed = canCancelDueToReturnedInvoice(order);
  // orders.change_status_blocked: libera SÓ o campo de status (Atendido/Enviado/Entregue sem NF).
  const statusUnlockedByPermission = canChangeStatusOfBlockedOrder(order, canChangeBlockedStatus);
  const isStatusFieldReadonly =
    isReadonly && !canChangeStatusOfDesmembradoParent && !statusEditableDespiteInvoice && !cancelWithReturnAllowed &&
    !statusUnlockedByPermission;
  // Cotação de frete (troca de transportadora): segue isReadonly, exceto pela permissão
  // orders.change_carrier_blocked em pedido Atendido sem NF.
  const isFreightQuoteReadonly = isReadonly && !canChangeCarrierOfBlockedOrder(order, canChangeCarrierBlocked);
  // Quando a devolução é a ÚNICA razão do destravamento, a única transição oferecida é o
  // cancelamento — a devolução não reabre o pedido para nenhum outro status.
  const statusChoicesLimitedToCancel =
    cancelWithReturnAllowed && isReadonly && !canChangeStatusOfDesmembradoParent && !statusEditableDespiteInvoice;
  // Pedido de venda ainda não pago: não avança para "Em Andamento"/"Atendido" nem emite NF de venda.
  // O backend rejeita as duas ações; aqui desabilitamos antes para o usuário entender o porquê.
  // Diferente de BlockOrderChange, este bloqueio não tem override por papel.
  const unpaidSaleBlocked = isUnpaidSaleOrder(order);
  // Child orders (filhos de pedido desmembrado) have items locked — inherited from the parent split
  // When order is paid (is_payed), only users with orders.edit_paid_items permission can edit items/replacement items
  const isPaidItemsBlocked = order.is_payed === true && !canEditPaidItems;
  // Desmembrado pai (status 12): pedido de escrituração cujos filhos carregam o envio real. Admin e
  // finance podem remover itens dele e recalcular — o backend (decideOrderEditable) agora aceita.
  // Liberamos os itens do PAI mesmo que ele esteja pago; isso não afeta o bloqueio de itens de
  // pedidos comuns pagos (que continua valendo). Ver canEditDesmembradoParentItems.
  const desmembradoParentItemsEditable = canEditDesmembradoParentItems(order, canOverrideBlockOrderChange);
  const isItemsReadonly =
    !desmembradoParentItemsEditable &&
    (isReadonly || !!order.parent_order_id || isPaidItemsBlocked);

  // ── Tab definitions ──────────────────────────────────────────

  const hasAnyReturnedQuantity = items.some(i => (i.returned_quantity ?? 0) > 0);
  const itemsColExtra = (canWrite && !isItemsReadonly ? 1 : 0);
  const itemsReturnedCols = hasAnyReturnedQuantity ? 2 : 0;

  const itemsTab: TabItem = {
    id: 'items',
    label: `Itens (${items.length})`,
    icon: <Package size={16} />,
    content: (
      <Card>
        {canWrite && !isItemsReadonly && (
          <div className="px-4 pt-4 pb-2 flex justify-end border-b border-slate-100">
            <Button size="sm" icon={<Plus size={15} />} onClick={startNewItem} disabled={itemShowNew || itemEditId !== null}>
              Novo Item
            </Button>
          </div>
        )}
        {order.parent_order_id && (
          <div className="px-4 py-3 border-b border-indigo-100 bg-indigo-50 text-sm text-indigo-700 flex items-center gap-2">
            <Info size={15} className="shrink-0" />
            Os itens deste pedido são herdados do pedido original e não podem ser alterados.
          </div>
        )}
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">SKU</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Qtd</th>
                  {hasAnyReturnedQuantity && <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase whitespace-nowrap">Qtd Devolvida</th>}
                  {hasAnyReturnedQuantity && <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase whitespace-nowrap">Qtd Efetiva</th>}
                  <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase whitespace-nowrap">Est. Físico</th>
                  <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase whitespace-nowrap" title="Estoque disponível para venda, descontando reservas de pedidos pendentes">Est. Disponível</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Unit.</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Desconto (%)</th>
                  <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Presente</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                  {canWrite && !isItemsReadonly && <th className="py-3 px-4 w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  itemEditId === item.order_item_id ? (
                    <tr key={item.order_item_id} className="border-b border-emerald-100 bg-emerald-50">
                      <td className="py-2 px-4 text-sm text-slate-500 font-medium">{itemForm.sku || '—'}</td>
                      <td className="py-2 px-4 relative min-w-[220px]">
                        <input
                          type="text"
                          className="w-full border border-slate-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          placeholder="Buscar produto por nome ou SKU..."
                          value={itemProductQuery}
                          onChange={e => handleItemProductSearch(e.target.value)}
                          onFocus={() => { if (itemProductQuery.trim()) setItemProductOpen(true); }}
                          onBlur={() => setTimeout(() => setItemProductOpen(false), 150)}
                        />
                        {itemProductOpen && (
                          <div className="absolute left-0 top-full z-50 bg-white border border-slate-200 rounded shadow-lg w-80 max-h-48 overflow-y-auto">
                            {itemProductLoading
                              ? <div className="p-3 text-sm text-slate-400 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Buscando...</div>
                              : itemProductResults.length === 0
                                ? <div className="p-3 text-sm text-slate-400 text-center">Nenhum produto encontrado</div>
                                : itemProductResults.map(p => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-slate-100 last:border-0"
                                    onMouseDown={() => handleItemProductSelect(p)}
                                  >
                                    <span className="font-medium text-slate-900">{p.sku}</span>
                                    <span className="text-slate-500 ml-2 text-xs">{p.description}</span>
                                    {p.stock_quantity_available != null && (
                                      <span className={`ml-2 text-xs font-medium ${p.stock_quantity_available < 0 ? 'text-red-600' : p.stock_quantity_available === 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                        | Disp: {p.stock_quantity_available}
                                      </span>
                                    )}
                                  </button>
                                ))
                            }
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right">
                        <input type="number" min="1" className="w-16 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={itemForm.quantity ?? 1} onChange={e => setItemForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
                        {itemSelectedStockAvailable !== null && (itemForm.quantity ?? 1) > itemSelectedStockAvailable && (
                          <p className="text-xs text-amber-600 mt-0.5" title="O pedido será criado, mas pode haver atraso na entrega.">
                            ⚠️ Excede disp. ({itemSelectedStockAvailable})
                          </p>
                        )}
                      </td>
                      {hasAnyReturnedQuantity && <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>}
                      {hasAnyReturnedQuantity && <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>}
                      <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>
                      <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>
                      <td className="py-2 px-4 text-right">
                        <input type="number" min="0" step="0.01" className="w-24 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={itemForm.unit_price ?? 0} onChange={e => setItemForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} />
                      </td>
                      <td className="py-2 px-4 text-right">
                        <input type="number" min="0" step="0.01" className="w-20 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={itemForm.discount ?? 0} onChange={e => setItemForm(f => ({ ...f, discount: parseFloat(e.target.value) || 0 }))} />
                      </td>
                      <td className="py-2 px-4 text-center">
                        <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" checked={!!itemForm.is_gift} onChange={e => setItemForm(f => ({ ...f, is_gift: e.target.checked }))} />
                      </td>
                      <td className="py-2 px-4 text-right text-slate-400 text-sm">—</td>
                      <td className="py-2 px-3">
                        <div className="flex gap-0.5">
                          <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={saveItemInline} disabled={itemSaving}>
                            {itemSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          </button>
                          <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={cancelItemEdit}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.order_item_id} className={`border-b border-slate-100 hover:bg-slate-50 ${item.is_fully_returned ? 'line-through opacity-60 text-gray-400' : ''}`}>
                      <td className="py-2 px-4 font-medium text-slate-900">{item.sku}</td>
                      <td className="py-2 px-4 text-slate-600">
                        <span className="flex items-center gap-2">
                          {item.description || '—'}
                          {(item.product_status === 'inactive' || item.product_status === 'missing') && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 no-underline"
                              style={{ textDecoration: 'none' }}
                              title={item.product_status === 'missing'
                                ? 'Este SKU não existe no cadastro de produtos. Edite o item e escolha o produto/variação correto antes de faturar.'
                                : 'Este SKU pertence a um produto INATIVO — a descrição exibida pode ser de outro produto. Edite o item e escolha a variação correta antes de faturar.'}
                            >
                              {item.product_status === 'missing' ? 'SKU sem produto' : 'SKU de produto inativo'}
                            </span>
                          )}
                          {item.is_fully_returned && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-600 no-underline" style={{ textDecoration: 'none' }}>Devolvido</span>
                          )}
                          {!item.is_fully_returned && (item.returned_quantity ?? 0) > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 no-underline" style={{ textDecoration: 'none' }}>Devolução parcial</span>
                          )}
                        </span>
                        {item.customization_text && (
                          <span className="block text-xs text-violet-700 mt-0.5">Personalização: {item.customization_text}</span>
                        )}
                        {item.is_custom_product && (
                          <span className="block text-xs text-pink-700 mt-0.5">
                            Produto personalizado
                            {item.has_project_photo && item.product_id ? (
                              <ProductProjectPhotoThumb productId={item.product_id} compact className="ml-2" />
                            ) : null}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right">{item.quantity}</td>
                      {hasAnyReturnedQuantity && (
                        <td className="py-2 px-4 text-right text-red-600 font-medium">{(item.returned_quantity ?? 0) > 0 ? item.returned_quantity : '—'}</td>
                      )}
                      {hasAnyReturnedQuantity && (
                        <td className="py-2 px-4 text-right font-medium text-emerald-700">
                          {item.effective_quantity ?? (item.quantity - (item.returned_quantity ?? 0))}
                          {!item.is_fully_returned && (item.returned_quantity ?? 0) > 0 && (
                            <span className="text-[10px] text-slate-400 ml-1">(devolvido: {item.returned_quantity})</span>
                          )}
                        </td>
                      )}
                      {(() => {
                        const sd = itemStockCache[item.sku];
                        const sqty = sd?.stock_quantity;
                        const savail = sd?.stock_quantity_available;
                        const hasDiff = sqty != null && savail != null && sqty !== savail;
                        const availColor = savail == null ? 'text-slate-400' : savail < 0 ? 'text-red-600' : savail === 0 ? 'text-amber-600' : (sd?.min_stock_quantity != null && savail < sd.min_stock_quantity) ? 'text-orange-600' : 'text-emerald-600';
                        return (
                          <>
                            <td className="py-2 px-4 text-center text-sm text-slate-700">{sqty != null ? sqty : '—'}</td>
                            <td className="py-2 px-4 text-center text-sm whitespace-nowrap">
                              {savail != null ? (
                                <span className={`font-medium ${availColor} inline-flex items-center gap-1`}>
                                  {savail < 0 && <AlertTriangle size={12} />}
                                  {savail}
                                  {hasDiff && sd?.product_id && (
                                    <button
                                      className="ml-1 text-emerald-600 hover:text-emerald-800 underline text-[10px] font-normal"
                                      title="Ver pedidos pendentes que reservam estoque"
                                      onClick={() => setPendingModal({ open: true, productId: sd.product_id, sku: item.sku, description: item.description })}
                                    >ver reservas</button>
                                  )}
                                </span>
                              ) : '—'}
                            </td>
                          </>
                        );
                      })()}
                      <td className="py-2 px-4 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="py-2 px-4 text-right">{item.discount ? `${item.discount}%` : '—'}</td>
                      <td className="py-2 px-4 text-center">
                        {item.is_gift ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-100 text-pink-700">Sim</span> : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="py-2 px-4 text-right font-medium">
                        {formatCurrency(item.effective_total ?? ((item.unit_price || 0) * (1 - (item.discount || 0) / 100) * (item.quantity - (item.returned_quantity ?? 0))))}
                      </td>
                      {canWrite && !isItemsReadonly && (
                        <td className="py-2 px-3">
                          <div className="flex gap-0.5">
                            <button className="p-1.5 text-slate-400 hover:text-emerald-600 transition-colors rounded" title="Editar" onClick={() => startEditItem(item)} disabled={itemEditId !== null || itemShowNew}>
                              <Pencil size={13} />
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" title="Excluir" onClick={() => setItemDeleteId(item.order_item_id!)}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                ))}
                {/* New item form row */}
                {itemShowNew && (
                  <tr className="border-b border-emerald-100 bg-emerald-50">
                    <td className="py-2 px-4 text-sm text-slate-500">{itemForm.sku || '—'}</td>
                    <td className="py-2 px-4 relative min-w-[220px]">
                      <input
                        type="text"
                        className="w-full border border-slate-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        placeholder="Buscar produto por nome ou SKU..."
                        value={itemProductQuery}
                        onChange={e => handleItemProductSearch(e.target.value)}
                        onFocus={() => { if (itemProductQuery.trim()) setItemProductOpen(true); }}
                        onBlur={() => setTimeout(() => setItemProductOpen(false), 150)}
                      />
                      {itemProductOpen && (
                        <div className="absolute left-0 top-full z-50 bg-white border border-slate-200 rounded shadow-lg w-80 max-h-48 overflow-y-auto">
                          {itemProductLoading
                            ? <div className="p-3 text-sm text-slate-400 text-center flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Buscando...</div>
                            : itemProductResults.length === 0
                              ? <div className="p-3 text-sm text-slate-400 text-center">Nenhum produto encontrado</div>
                              : itemProductResults.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 border-b border-slate-100 last:border-0"
                                  onMouseDown={() => handleItemProductSelect(p)}
                                >
                                  <span className="font-medium text-slate-900">{p.sku}</span>
                                  <span className="text-slate-500 ml-2 text-xs">{p.description}</span>
                                  {p.stock_quantity_available != null && (
                                    <span className={`ml-2 text-xs font-medium ${p.stock_quantity_available < 0 ? 'text-red-600' : p.stock_quantity_available === 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                      | Disp: {p.stock_quantity_available}
                                    </span>
                                  )}
                                </button>
                              ))
                          }
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="1" className="w-16 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={itemForm.quantity ?? 1} onChange={e => setItemForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
                      {itemSelectedStockAvailable !== null && (itemForm.quantity ?? 1) > itemSelectedStockAvailable && (
                        <p className="text-xs text-amber-600 mt-0.5" title="O pedido será criado, mas pode haver atraso na entrega.">
                          ⚠️ Excede disp. ({itemSelectedStockAvailable})
                        </p>
                      )}
                    </td>
                    {hasAnyReturnedQuantity && <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>}
                    {hasAnyReturnedQuantity && <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>}
                    <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>
                    <td className="py-2 px-4 text-center text-slate-300 text-xs">—</td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="0" step="0.01" className="w-24 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={itemForm.unit_price ?? 0} onChange={e => setItemForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="0" step="0.01" className="w-20 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={itemForm.discount ?? 0} onChange={e => setItemForm(f => ({ ...f, discount: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <input type="checkbox" className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" checked={!!itemForm.is_gift} onChange={e => setItemForm(f => ({ ...f, is_gift: e.target.checked }))} />
                    </td>
                    <td className="py-2 px-4 text-right text-slate-400 text-sm">—</td>
                    <td className="py-2 px-3">
                      <div className="flex gap-0.5">
                        <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={saveItemInline} disabled={itemSaving}>
                          {itemSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        </button>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={cancelItemEdit}>
                          <X size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {items.length === 0 && !itemShowNew && (
                  <tr>
                    <td colSpan={7 + itemsColExtra + itemsReturnedCols} className="py-8 text-center text-slate-400">Nenhum item</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                <tr>
                  <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-3 px-4 text-right font-semibold text-slate-700 uppercase text-sm">Total Produtos</td>
                  <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(order.order_products_total)}</td>
                  {canWrite && !isItemsReadonly && <td />}
                </tr>
                <tr>
                  <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-1 px-4 text-right text-sm text-slate-600">Frete</td>
                  <td className="py-1 px-4 text-right text-slate-900">{formatCurrency(order.shipping_price)}</td>
                  {canWrite && !isItemsReadonly && <td />}
                </tr>
                {(order.freight_discount_value ?? 0) > 0 && (
                  <tr>
                    <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-1 px-4 text-right text-sm text-red-600">Desconto no Frete</td>
                    <td className="py-1 px-4 text-right text-red-600">-{formatCurrency(order.freight_discount_value)}</td>
                    {canWrite && !isItemsReadonly && <td />}
                  </tr>
                )}
                <tr>
                  <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-1 px-4 text-right text-sm text-slate-600">Desconto</td>
                  <td className="py-1 px-4 text-right text-red-600">-{formatCurrency(order.order_discount)}</td>
                  {canWrite && !isItemsReadonly && <td />}
                </tr>
                {(order.product_replacement_discount_value ?? 0) > 0 && (
                  <tr>
                    <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-1 px-4 text-right text-sm text-purple-600">Desc. Troca/Devolução</td>
                    <td className="py-1 px-4 text-right text-purple-600">-{formatCurrency(order.product_replacement_discount_value)}</td>
                    {canWrite && !isItemsReadonly && <td />}
                  </tr>
                )}
                <tr>
                  <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-1 px-4 text-right text-sm text-slate-600">Outras Despesas</td>
                  <td className="py-1 px-4 text-right text-orange-600">{formatCurrency(order.other_expenses)}</td>
                  {canWrite && !isItemsReadonly && <td />}
                </tr>
                <tr className="border-t border-slate-300">
                  <td colSpan={8 + itemsColExtra + itemsReturnedCols} className="py-3 px-4 text-right font-bold text-slate-900 uppercase">Total Geral</td>
                  <td className={`py-3 px-4 text-right font-bold text-lg whitespace-nowrap ${(order.order_sell_total ?? 0) < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{formatCurrency(order.order_sell_total)}</td>
                  {canWrite && !isItemsReadonly && <td />}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    ),
  };

  const inlineNumInput = (
    field: keyof VolumePayload,
    placeholder?: string,
  ) => (
    <input
      type="number"
      min="0"
      step="0.01"
      placeholder={placeholder}
      className="w-20 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
      value={volumeForm[field] ?? 0}
      onChange={e => setVolumeForm(f => ({ ...f, [field]: parseFloat(e.target.value) || 0 }))}
    />
  );

  const volumesTab: TabItem = {
    id: 'volumes',
    label: `Volumes (${volumes.length})`,
    icon: <Box size={16} />,
    content: (
      <div className="space-y-4">
        <Card>
          {canManageVolumes && !isReadonly && (
            <div className="px-4 pt-4 pb-2 flex justify-end border-b border-slate-100">
              <Button size="sm" icon={<Plus size={15} />} onClick={startNewVolume} disabled={volumeShowNew || volumeEditId !== null}>
                Novo Volume
              </Button>
            </div>
          )}
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase w-10">#</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Altura (cm)</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Largura (cm)</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Compr. (cm)</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Diâmetro (cm)</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Peso (kg)</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Itens</th>
                    {canManageVolumes && !isReadonly && <th className="py-3 px-4 w-28"></th>}
                  </tr>
                </thead>
                <tbody>
                  {volumes.map((vol, idx) => (
                    <React.Fragment key={vol.order_volume_id}>
                    {volumeEditId === vol.order_volume_id ? (
                      <tr className="border-b border-emerald-100 bg-emerald-50">
                        <td className="py-2 px-4 text-slate-400">{vol.volume_number ?? idx + 1}</td>
                        <td className="py-2 px-4 text-right">{inlineNumInput('height')}</td>
                        <td className="py-2 px-4 text-right">{inlineNumInput('width')}</td>
                        <td className="py-2 px-4 text-right">{inlineNumInput('length')}</td>
                        <td className="py-2 px-4 text-right">{inlineNumInput('diameter')}</td>
                        <td className="py-2 px-4 text-right">{inlineNumInput('weight')}</td>
                        <td className="py-2 px-4"></td>
                        <td className="py-2 px-3">
                          <div className="flex gap-0.5">
                            <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={saveVolume} disabled={volumeSaving}>
                              {volumeSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={cancelVolumeEdit}>
                              <X size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-4 font-medium text-slate-900">{vol.volume_number ?? idx + 1}</td>
                        <td className="py-2 px-4 text-right">{vol.height}</td>
                        <td className="py-2 px-4 text-right">{vol.width}</td>
                        <td className="py-2 px-4 text-right">{vol.length}</td>
                        <td className="py-2 px-4 text-right">{vol.diameter || '—'}</td>
                        <td className="py-2 px-4 text-right">{vol.weight}</td>
                        <td className="py-2 px-4">
                          {(vol.items?.length ?? 0) > 0 ? (
                            <span className="text-xs text-slate-600">
                              {vol.items!.length} {vol.items!.length === 1 ? 'item' : 'itens'}
                            </span>
                          ) : (
                            <span
                              className="text-[11px] italic text-slate-400"
                              title="Sem atribuição de itens, o valor declarado do frete é rateado pelo peso"
                            >
                              Rateio automático por peso
                            </span>
                          )}
                        </td>
                        {canManageVolumes && !isReadonly && (
                          <td className="py-2 px-3">
                            <div className="flex gap-0.5">
                              <button
                                className={`p-1.5 transition-colors rounded ${volumeItemsOpenId === vol.order_volume_id ? 'text-emerald-600' : 'text-slate-400 hover:text-emerald-600'}`}
                                title="Itens do volume"
                                onClick={() => toggleVolumeItems(vol)}
                              >
                                <Package size={13} />
                              </button>
                              <button className="p-1.5 text-slate-400 hover:text-emerald-600 transition-colors rounded" title="Editar" onClick={() => startEditVolume(vol)}>
                                <Pencil size={13} />
                              </button>
                              <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" title="Excluir" onClick={() => setVolumeDeleteId(vol.order_volume_id!)}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    )}
                    {/* Inline expansion: atribuição opcional de itens ao volume */}
                    {volumeItemsOpenId === vol.order_volume_id && canManageVolumes && !isReadonly && (
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <td colSpan={8} className="py-3 px-6">
                          <div className="space-y-2">
                            <div className="text-xs font-medium text-slate-600 uppercase">
                              Itens do volume {vol.volume_number ?? idx + 1}
                            </div>
                            {items.length === 0 ? (
                              <div className="text-sm text-slate-400">Nenhum item no pedido</div>
                            ) : (
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="text-xs text-slate-500">
                                    <th className="py-1 pr-2 text-left font-medium">SKU</th>
                                    <th className="py-1 pr-2 text-left font-medium">Produto</th>
                                    <th className="py-1 pr-2 text-right font-medium">Qtd. pedido</th>
                                    <th className="py-1 pr-2 text-right font-medium">Em outros volumes</th>
                                    <th className="py-1 pr-2 text-right font-medium">Neste volume</th>
                                    <th className="py-1 text-right font-medium">Valor declarado (R$)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map(item => {
                                    const elsewhere = allocatedInOtherVolumes(item.order_item_id!, vol.order_volume_id!);
                                    const maxQty = Math.max(0, item.quantity - elsewhere);
                                    const f = volumeItemsForm[item.order_item_id!] ?? { quantity: '', declared_value: '' };
                                    return (
                                      <tr key={item.order_item_id} className="border-t border-slate-200">
                                        <td className="py-1.5 pr-2 font-mono text-xs text-slate-700">{item.sku}</td>
                                        <td className="py-1.5 pr-2 text-slate-700">{item.description || '—'}</td>
                                        <td className="py-1.5 pr-2 text-right text-slate-500">{item.quantity}</td>
                                        <td className="py-1.5 pr-2 text-right text-slate-500">{elsewhere}</td>
                                        <td className="py-1.5 pr-2 text-right">
                                          <input
                                            type="number"
                                            min={0}
                                            max={maxQty}
                                            step={1}
                                            className="w-16 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                            value={f.quantity}
                                            placeholder="0"
                                            onChange={e => setVolumeItemField(item.order_item_id!, 'quantity', e.target.value)}
                                          />
                                        </td>
                                        <td className="py-1.5 text-right">
                                          <input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            className="w-24 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                            value={f.declared_value}
                                            placeholder="auto"
                                            title="Opcional — em branco, calculado a partir do preço do item"
                                            onChange={e => setVolumeItemField(item.order_item_id!, 'declared_value', e.target.value)}
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                            {volumeItemsError && (
                              <div className="text-xs text-red-600">{volumeItemsError}</div>
                            )}
                            <div className="flex justify-end gap-2 pt-1">
                              <Button size="sm" variant="outline" onClick={cancelVolumeItems} disabled={volumeItemsSaving}>
                                Cancelar
                              </Button>
                              <Button
                                size="sm"
                                icon={volumeItemsSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                                onClick={saveVolumeItems}
                                disabled={volumeItemsSaving}
                              >
                                Salvar
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                  {/* New volume form row */}
                  {volumeShowNew && (
                    <tr className="border-b border-emerald-100 bg-emerald-50">
                      <td className="py-2 px-4 text-slate-400">—</td>
                      <td className="py-2 px-4 text-right">{inlineNumInput('height', 'Alt.')}</td>
                      <td className="py-2 px-4 text-right">{inlineNumInput('width', 'Larg.')}</td>
                      <td className="py-2 px-4 text-right">{inlineNumInput('length', 'Comp.')}</td>
                      <td className="py-2 px-4 text-right">{inlineNumInput('diameter', 'Diâm.')}</td>
                      <td className="py-2 px-4 text-right">{inlineNumInput('weight', 'Peso')}</td>
                      <td className="py-2 px-4"></td>
                      <td className="py-2 px-3">
                        <div className="flex gap-0.5">
                          <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={saveVolume} disabled={volumeSaving}>
                            {volumeSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          </button>
                          <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={cancelVolumeEdit}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {volumes.length === 0 && !volumeShowNew && (
                    <tr>
                      <td colSpan={canManageVolumes && !isReadonly ? 8 : 7} className="py-8 text-center text-slate-400">Nenhum volume cadastrado</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* ── Freight Quote Panel ────────────────────────────────── */}
        {volumes.length > 0 && (
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <Truck size={16} className="text-emerald-600" />
                  Cotação de Frete
                </span>
              }
              action={
                /* Sem orders.manage_volumes o endpoint devolve 403; mostrar o botão só troca
                   um "você não tem permissão" por um erro sem explicação. */
                !isFreightQuoteReadonly && canManageVolumes ? (
                  <Button
                    size="sm"
                    variant="outline"
                    icon={freightCalculating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    onClick={handleCalculateFreight}
                    disabled={freightCalculating || freightLoading}
                  >
                    {freightQuotes.length > 0 ? 'Recalcular' : 'Calcular Frete'}
                  </Button>
                ) : undefined
              }
            />
            <CardContent>
              {freightError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
                  <AlertTriangle size={14} />
                  {freightError}
                </div>
              )}

              {freightLoading || freightCalculating ? (
                <div className="flex items-center justify-center py-8 gap-2 text-slate-400">
                  <Loader2 size={20} className="animate-spin" />
                  <span>{freightCalculating ? 'Calculando cotações de frete...' : 'Carregando cotações...'}</span>
                </div>
              ) : freightQuotes.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Truck size={32} className="mx-auto mb-2 opacity-40" />
                  <p>Nenhuma cotação de frete disponível.</p>
                  <p className="text-xs mt-1">Clique em "Calcular Frete" para obter cotações.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {freightQuotes.map(quote => {
                    const isSelected = quote.is_selected;
                    const blocked = isBlockedQuote(quote);
                    return (
                      <label
                        key={quote.order_freight_quote_id}
                        title={blocked ? quoteBlockReason(quote) : undefined}
                        className={`flex items-center gap-4 p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50'
                            : blocked
                              ? 'border-red-200 bg-red-50/40'
                              : 'border-slate-200 hover:border-slate-300 bg-white'
                        } ${isFreightQuoteReadonly || freightSelectSaving || blocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="freight_quote"
                          checked={isSelected}
                          disabled={isFreightQuoteReadonly || freightSelectSaving || blocked}
                          onChange={() => handleSelectFreightQuote(quote.order_freight_quote_id)}
                          className="w-4 h-4 text-emerald-600 border-slate-300 focus:ring-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{quote.provider_name || `Provedor #${quote.provider_id}`}</span>
                            <span className="text-xs text-slate-500">•</span>
                            <span className="text-sm text-slate-600">{quote.provider_service_name || `Serviço #${quote.provider_service_id}`}</span>
                            {isQuoteChosenByCustomer(order, quote) && (
                              <span
                                className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                                title="Transportadora e serviço que o cliente escolheu no checkout"
                              >
                                Escolhida pelo cliente
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span>Prazo: <strong className="text-slate-700">{formatFreightQuoteDays(quote)}</strong></span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`text-lg font-bold ${isSelected ? 'text-emerald-700' : 'text-slate-900'}`}>
                            {formatFreightQuoteCost(quote)}
                          </span>
                        </div>
                        {isSelected && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            Selecionado
                          </span>
                        )}
                        {blocked && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
                            title={quoteBlockReason(quote)}
                          >
                            {BLOCKED_OPTION_BADGE}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    ),
  };

  const costTab: TabItem = {
    id: 'cost',
    label: 'Custo e Margem',
    icon: <DollarSign size={16} />,
    content: (
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <tbody>
              {/* ── Receita ── */}
              <tr className="bg-slate-50">
                <td colSpan={3} className="py-2 px-4 font-semibold text-slate-700 uppercase text-xs tracking-wider">Receita</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Total Produtos</td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">{formatCurrency(order.order_products_total)}</td>
                <td className="py-2 px-4 w-10"></td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Frete</td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">+ {formatCurrency(order.shipping_price)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              {(order.freight_discount_value ?? 0) > 0 && (
                <tr className="border-b border-slate-100">
                  <td className="py-2 px-4 text-slate-600">Desconto no Frete</td>
                  <td className="py-2 px-4 text-right text-red-600 tabular-nums">− {formatCurrency(order.freight_discount_value)}</td>
                  <td className="py-2 px-4"></td>
                </tr>
              )}
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Desconto</td>
                <td className="py-2 px-4 text-right text-red-600 tabular-nums">− {formatCurrency(order.order_discount)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              {(order.product_replacement_discount_value ?? 0) > 0 && (
                <tr className="border-b border-slate-100">
                  <td className="py-2 px-4 text-purple-600">Desc. Troca/Devolução</td>
                  <td className="py-2 px-4 text-right text-purple-600 tabular-nums">− {formatCurrency(order.product_replacement_discount_value)}</td>
                  <td className="py-2 px-4"></td>
                </tr>
              )}
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Outras Despesas</td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">+ {formatCurrency(order.other_expenses)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b-2 border-slate-300 bg-emerald-50">
                <td className="py-2.5 px-4 font-semibold text-slate-800">= Total Venda</td>
                <td className="py-2.5 px-4 text-right font-bold text-emerald-700 text-base tabular-nums">{formatCurrency(order.order_sell_total)}</td>
                <td className="py-2.5 px-4"></td>
              </tr>

              {/* ── Custos ── */}
              <tr className="bg-slate-50">
                <td colSpan={3} className="py-2 px-4 font-semibold text-slate-700 uppercase text-xs tracking-wider mt-2">Custos</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Custo dos Produtos</td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">{formatCurrency(order.product_cost_sum)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">
                  Impostos
                  {order.tax_percent != null && <span className="text-slate-400 text-xs ml-1">({formatPercent(order.tax_percent)})</span>}
                </td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">+ {formatCurrency(order.tax_value)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">
                  Comissão Vendedor
                  {order.seller_commission_percent != null && <span className="text-slate-400 text-xs ml-1">({formatPercent(order.seller_commission_percent)})</span>}
                </td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">+ {formatCurrency(order.seller_commission_value)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">
                  Comissão PO
                  {order.po_commission_percent != null && <span className="text-slate-400 text-xs ml-1">({formatPercent(order.po_commission_percent)})</span>}
                </td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">+ {formatCurrency(order.po_commission_value)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">
                  Taxa Forma Pgto
                  {order.payment_method_fee_percent != null && <span className="text-slate-400 text-xs ml-1">({formatPercent(order.payment_method_fee_percent)})</span>}
                </td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">+ {formatCurrency(order.payment_method_fee)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b-2 border-slate-300 bg-orange-50">
                <td className="py-2.5 px-4 font-semibold text-slate-800">= Custo Total</td>
                <td className="py-2.5 px-4 text-right font-bold text-orange-700 text-base tabular-nums">{formatCurrency(order.total_order_cost)}</td>
                <td className="py-2.5 px-4"></td>
              </tr>

              {/* ── Resultado ── */}
              <tr className="bg-slate-50">
                <td colSpan={3} className="py-2 px-4 font-semibold text-slate-700 uppercase text-xs tracking-wider">Resultado</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Total Venda</td>
                <td className="py-2 px-4 text-right text-slate-900 tabular-nums">{formatCurrency(order.order_sell_total)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 px-4 text-slate-600">Custo Total</td>
                <td className="py-2 px-4 text-right text-red-600 tabular-nums">− {formatCurrency(order.total_order_cost)}</td>
                <td className="py-2 px-4"></td>
              </tr>
              <tr className={`${order.order_earn && order.order_earn > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                <td className="py-3 px-4 font-semibold text-slate-800">= Margem</td>
                <td className={`py-3 px-4 text-right font-bold text-base tabular-nums ${order.order_earn && order.order_earn > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(order.order_earn)}
                </td>
                <td className={`py-3 px-4 text-right text-xs font-semibold ${order.order_earn && order.order_earn > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {order.order_earn_percent != null ? `${(order.order_earn_percent * 100).toFixed(1)}%` : '—'}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
        {canViewFinancials && (
          <div className="px-4 py-3 border-t border-slate-200 flex justify-end">
            <button
              type="button"
              disabled={recalculating}
              onClick={async () => {
                try {
                  setRecalculating(true);
                  const updated = await salesOrderService.recalculateOrder(order.order_id);
                  setOrder(updated);
                } catch (err) {
                  setActionError(err ?? 'Erro ao recalcular pedido');
                } finally {
                  setRecalculating(false);
                }
              }}
              className="inline-flex items-center gap-2 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded px-4 py-2 disabled:opacity-50 transition-colors"
            >
              {recalculating ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {recalculating ? 'Recalculando...' : 'Forçar Recálculo'}
            </button>
          </div>
        )}
      </Card>
    ),
  };

  const installmentsTab: TabItem = {
    id: 'installments',
    label: `Parcelas (${installments.length})`,
    icon: <CreditCard size={16} />,
    content: (
      <Card>
        {canWrite && !isReadonly && (
          <div className="px-4 pt-4 pb-2 flex justify-end border-b border-slate-100">
            <Button size="sm" icon={<Plus size={15} />} onClick={startNewInstallment} disabled={installmentShowNew || installmentEditId !== null}>
              Nova Parcela
            </Button>
          </div>
        )}
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">#</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Vencimento</th>
                  <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Forma de Pgto.</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Observação</th>
                  {canWrite && !isReadonly && <th className="py-3 px-4 w-16"></th>}
                </tr>
              </thead>
              <tbody>
                {installments.map((inst, idx) => (
                  installmentEditId === inst.order_installment_id ? (
                    <tr key={inst.order_installment_id} className="border-b border-emerald-100 bg-emerald-50">
                      <td className="py-2 px-4 text-slate-400">{idx + 1}</td>
                      <td className="py-2 px-4">
                        <DateInputBR className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.due_date ?? ''} onChange={v => setInstallmentForm(f => ({ ...f, due_date: v }))} />
                      </td>
                      <td className="py-2 px-4 text-right">
                        <input type="number" min="0" step="0.01" className="w-28 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.value ?? 0} onChange={e => setInstallmentForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))} />
                      </td>
                      <td className="py-2 px-4">
                        <select className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.payment_method_id ?? ''} onChange={e => { const id = parseInt(e.target.value); const pm = paymentMethods.find(p => p.payment_method_id === id); setInstallmentForm(f => ({ ...f, payment_method_id: id || undefined, payment_method_name: pm?.payment_method_name ?? '' })); }}>
                          <option value="">-- Selecione --</option>
                          {paymentMethods.map(pm => (
                            <option key={pm.payment_method_id} value={pm.payment_method_id}>{pm.payment_method_name}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 px-4">
                        <input className="border border-slate-300 rounded px-1 py-0.5 text-sm w-36 focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.notes ?? ''} onChange={e => setInstallmentForm(f => ({ ...f, notes: e.target.value }))} placeholder="Obs." />
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex gap-0.5">
                          <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={saveInstallmentInline} disabled={installmentSaving}>
                            {installmentSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                          </button>
                          <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={cancelInstallmentEdit}>
                            <X size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={inst.order_installment_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-4 font-medium text-slate-900">{idx + 1}</td>
                      <td className="py-2 px-4">{formatDate(inst.due_date)}</td>
                      <td className="py-2 px-4 text-right font-medium">{formatCurrency(inst.value)}</td>
                      <td className="py-2 px-4 text-slate-600">{inst.payment_method_name || '—'}</td>
                      <td className="py-2 px-4 text-slate-600">{inst.notes || '—'}</td>
                      {canWrite && !isReadonly && (
                        <td className="py-2 px-3">
                          <div className="flex gap-0.5">
                            <button className="p-1.5 text-slate-400 hover:text-emerald-600 transition-colors rounded" title="Editar" onClick={() => startEditInstallment(inst)} disabled={installmentEditId !== null || installmentShowNew}>
                              <Pencil size={13} />
                            </button>
                            <button className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded" title="Excluir" onClick={() => setInstallmentDeleteId(inst.order_installment_id!)}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                ))}
                {/* New installment form row */}
                {installmentShowNew && (
                  <tr className="border-b border-emerald-100 bg-emerald-50">
                    <td className="py-2 px-4 text-slate-400">—</td>
                    <td className="py-2 px-4">
                      <DateInputBR className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.due_date ?? ''} onChange={v => setInstallmentForm(f => ({ ...f, due_date: v }))} />
                    </td>
                    <td className="py-2 px-4 text-right">
                      <input type="number" min="0" step="0.01" className="w-28 text-right border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.value ?? 0} onChange={e => setInstallmentForm(f => ({ ...f, value: parseFloat(e.target.value) || 0 }))} />
                    </td>
                    <td className="py-2 px-4">
                      <select className="border border-slate-300 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.payment_method_id ?? ''} onChange={e => { const id = parseInt(e.target.value); const pm = paymentMethods.find(p => p.payment_method_id === id); setInstallmentForm(f => ({ ...f, payment_method_id: id || undefined, payment_method_name: pm?.payment_method_name ?? '' })); }}>
                        <option value="">-- Selecione --</option>
                        {paymentMethods.map(pm => (
                          <option key={pm.payment_method_id} value={pm.payment_method_id}>{pm.payment_method_name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-4">
                      <input className="border border-slate-300 rounded px-1 py-0.5 text-sm w-36 focus:outline-none focus:ring-1 focus:ring-emerald-500" value={installmentForm.notes ?? ''} onChange={e => setInstallmentForm(f => ({ ...f, notes: e.target.value }))} placeholder="Obs." />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-0.5">
                        <button className="p-1.5 text-emerald-600 hover:text-emerald-800 transition-colors rounded" title="Salvar" onClick={saveInstallmentInline} disabled={installmentSaving}>
                          {installmentSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                        </button>
                        <button className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors rounded" title="Cancelar" onClick={cancelInstallmentEdit}>
                          <X size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {installments.length === 0 && !installmentShowNew && (
                  <tr>
                    <td colSpan={canWrite && !isReadonly ? 6 : 5} className="py-8 text-center text-slate-400">Nenhuma parcela</td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                {(() => {
                  const returnCreditPm = paymentMethods.find(pm => pm.is_product_credit_4_return);
                  const returnCreditInstallments = returnCreditPm
                    ? installments.filter(i => i.payment_method_id === returnCreditPm.payment_method_id)
                    : [];
                  const returnCreditTotal = returnCreditInstallments.reduce((s, i) => s + i.value, 0);
                  const rawTotal = installments.reduce((s, i) => s + i.value, 0);
                  const displayTotal = rawTotal - returnCreditTotal;
                  return (
                    <>
                      <tr>
                        <td colSpan={2} className="py-3 px-4 text-right font-semibold text-slate-700">Total Parcelas</td>
                        <td className="py-3 px-4 text-right font-bold text-slate-900">
                          {formatCurrency(rawTotal)}
                        </td>
                        <td colSpan={canWrite && !isReadonly ? 3 : 2}></td>
                      </tr>
                      {returnCreditTotal > 0 && (
                        <>
                          <tr>
                            <td colSpan={2} className="py-1 px-4 text-right text-sm text-purple-600">Crédito Troca/Devolução</td>
                            <td className="py-1 px-4 text-right text-purple-600">-{formatCurrency(returnCreditTotal)}</td>
                            <td colSpan={canWrite && !isReadonly ? 3 : 2}></td>
                          </tr>
                          <tr className="border-t border-slate-300">
                            <td colSpan={2} className="py-3 px-4 text-right font-bold text-slate-900">Total a Pagar</td>
                            <td className="py-3 px-4 text-right font-bold text-emerald-700 text-base">
                              {formatCurrency(displayTotal)}
                            </td>
                            <td colSpan={canWrite && !isReadonly ? 3 : 2}></td>
                          </tr>
                        </>
                      )}
                    </>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    ),
  };

  // ─── Tab: Retirada para Troca ───────────────────────────────────
  const canWriteReplacementItems = canWrite && !isPaidItemsBlocked;
  const replacementTab: TabItem = {
    id: 'replacements',
    label: `Retirada p/ Troca${replacementItems.length ? ` (${replacementItems.length})` : ''}`,
    content: (
      <Card>
        <CardHeader
          title="Itens para Retirada (Troca)"
          action={
            canWriteReplacementItems ? (
              <Button
                variant="secondary"
                className="text-xs"
                onClick={async () => {
                  setReplacementModalOpen(true);
                  if (order.customer_id && customerOrders.length === 0) {
                    setCustomerOrdersLoading(true);
                    try {
                      const result = await salesOrderService.getCustomerOrders(order.customer_id, order.order_id);
                      setCustomerOrders(result);
                    } catch { /* ignore */ }
                    finally { setCustomerOrdersLoading(false); }
                  }
                }}
              >
                <Plus size={14} /> Adicionar itens de troca
              </Button>
            ) : undefined
          }
        />
        <CardContent>
          {replacementLoading ? (
            <div className="flex justify-center py-6"><RefreshCw size={20} className="animate-spin text-slate-400" /></div>
          ) : replacementItems.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-4 text-center">
              Nenhum item de retirada (troca) vinculado a este pedido.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Pedido Original</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">SKU</th>
                    <th className="py-2 px-4 text-left text-xs font-medium text-slate-500 uppercase">Descrição</th>
                    <th className="py-2 px-4 text-center text-xs font-medium text-slate-500 uppercase">Qtd</th>
                    <th className="py-2 px-4 text-right text-xs font-medium text-slate-500 uppercase">Valor Original</th>
                    <th className="py-2 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                    {canWriteReplacementItems && <th className="py-2 px-4 text-right text-xs font-medium text-slate-500 uppercase">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {replacementItems.map(item => (
                    <tr key={item.replacement_order_item_id ?? `${item.original_order_id}-${item.sku}`} className="border-b border-slate-100">
                      <td className="py-2 px-4 font-mono text-xs text-slate-600">#{item.original_order_id}</td>
                      <td className="py-2 px-4 font-mono text-xs font-semibold text-slate-800">{item.sku}</td>
                      <td className="py-2 px-4 text-slate-700">{item.description || '—'}</td>
                      <td className="py-2 px-4 text-center font-medium">{item.quantity}</td>
                      <td className="py-2 px-4 text-right text-slate-700">{formatCurrency(item.original_value)}</td>
                      <td className="py-2 px-4 text-right font-medium text-slate-900">{formatCurrency((item.original_value ?? 0) * item.quantity)}</td>
                      {canWriteReplacementItems && (
                        <td className="py-2 px-4 text-right">
                          <button
                            type="button"
                            onClick={async () => {
                              if (!item.replacement_order_item_id) return;
                              try {
                                await salesOrderService.deleteReplacementItem(order.order_id, item.replacement_order_item_id);
                                const remaining = replacementItems.filter(r => r.replacement_order_item_id !== item.replacement_order_item_id);
                                setReplacementItems(remaining);
                                // Recalculate product_replacement_discount_value
                                const totalReplacementDiscount = remaining.reduce(
                                  (sum, ri) => sum + (ri.original_value ?? 0) * ri.quantity, 0
                                );
                                try {
                                  const updated = await salesOrderService.updateOrder(order.order_id, {
                                    product_replacement_discount_value: Math.round(totalReplacementDiscount * 10000) / 10000,
                                  }, order);
                                  setOrder(updated);
                                } catch { /* server-side recalc */ }
                              } catch (err) {
                                setReplacementError(err instanceof Error ? err.message : 'Erro ao remover');
                              }
                            }}
                            className="p-1 text-slate-400 hover:text-red-500"
                            title="Remover item de troca"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-purple-50 border-t-2 border-slate-200">
                  <tr>
                    <td colSpan={3} className="py-3 px-4 text-right font-semibold text-purple-700 uppercase text-sm">Total Desc. Troca</td>
                    <td className="py-3 px-4 text-center font-medium text-purple-700">{replacementItems.reduce((s, i) => s + i.quantity, 0)}</td>
                    <td className="py-3 px-4 text-right text-purple-600">—</td>
                    <td className="py-3 px-4 text-right font-bold text-purple-700">
                      {formatCurrency(replacementItems.reduce((s, i) => s + (i.original_value ?? 0) * i.quantity, 0))}
                    </td>
                    {canWriteReplacementItems && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          {replacementError && (
            <div className="mt-2 text-xs text-red-500">{replacementError}</div>
          )}
        </CardContent>
      </Card>
    ),
  };

  // ─── Modal: selecionar itens do pedido original ──────────────────
  const replacementModal = replacementModalOpen && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={e => { if (e.target === e.currentTarget) setReplacementModalOpen(false); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-800">Selecionar itens para troca</h2>
          <button type="button" onClick={() => setReplacementModalOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {customerOrdersLoading ? (
            <div className="flex justify-center py-8"><RefreshCw size={20} className="animate-spin text-slate-400" /></div>
          ) : customerOrders.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Nenhum pedido anterior encontrado para este cliente.</p>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 mb-3">Selecione um pedido e marque os itens que o cliente deseja devolver:</p>
              {customerOrders.map(co => (
                <div key={co.order_id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={async () => {
                      if (expandedOrderId === co.order_id) {
                        setExpandedOrderId(null);
                        return;
                      }
                      setExpandedOrderId(co.order_id);
                      setExpandedOrderItemsLoading(true);
                      try {
                        const detail = await salesOrderService.getOrderById(co.order_id);
                        setExpandedOrderItems(detail.items ?? []);
                        setExpandedOrderData({
                          order_discount: detail.order_discount,
                          order_products_total: detail.order_products_total,
                        });
                      } catch { setExpandedOrderItems([]); setExpandedOrderData(null); }
                      finally { setExpandedOrderItemsLoading(false); }
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-slate-800 text-sm">#{co.order_id}</span>
                      <span className="text-xs text-slate-500">{new Date(co.order_date).toLocaleDateString('pt-BR')}</span>
                      {co.order_status && <span className="text-xs bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">{co.order_status}</span>}
                    </div>
                    <span className="text-slate-400">{expandedOrderId === co.order_id ? '▼' : '▶'}</span>
                  </button>
                  {expandedOrderId === co.order_id && (
                    <div className="px-4 py-3 border-t border-slate-100">
                      {expandedOrderItemsLoading ? (
                        <div className="flex justify-center py-4"><RefreshCw size={16} className="animate-spin text-slate-400" /></div>
                      ) : expandedOrderItems.length === 0 ? (
                        <p className="text-xs text-slate-400">Nenhum item neste pedido.</p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500 uppercase">
                              <th className="py-1 px-2 text-left w-8"></th>
                              <th className="py-1 px-2 text-left">SKU</th>
                              <th className="py-1 px-2 text-left">Descrição</th>
                              <th className="py-1 px-2 text-right">Valor Unit.</th>
                              <th className="py-1 px-2 text-right">Valor Final</th>
                              <th className="py-1 px-2 text-center">Qtd Enviada</th>
                              <th className="py-1 px-2 text-center">Qtd Devolver</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expandedOrderItems.map(item => {
                              const key = `${co.order_id}:${item.sku}`;
                              const selected = selectedReplacements[key] ?? 0;
                              // Calculate item original value considering individual and global discounts
                              const unitPrice = item.unit_price || 0;
                              const itemDiscount = item.discount || 0;
                              const priceAfterItemDiscount = unitPrice * (1 - itemDiscount / 100);
                              // Global discount proportion
                              const orderDiscount = expandedOrderData?.order_discount ?? 0;
                              const orderProductsTotal = expandedOrderData?.order_products_total ?? 0;
                              const globalDiscountPercent = orderProductsTotal > 0 && orderDiscount > 0
                                ? orderDiscount / orderProductsTotal
                                : 0;
                              const finalItemValue = Math.round(priceAfterItemDiscount * (1 - globalDiscountPercent) * 10000) / 10000;
                              return (
                                <tr key={item.sku} className="border-t border-slate-50">
                                  <td className="py-1.5 px-2">
                                    <input
                                      type="checkbox"
                                      checked={selected > 0}
                                      onChange={e => {
                                        setSelectedReplacements(prev => ({
                                          ...prev,
                                          [key]: e.target.checked ? 1 : 0,
                                        }));
                                      }}
                                      className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                    />
                                  </td>
                                  <td className="py-1.5 px-2 font-mono font-semibold text-slate-800">{item.sku}</td>
                                  <td className="py-1.5 px-2 text-slate-700">{item.description || '—'}</td>
                                  <td className="py-1.5 px-2 text-right text-slate-500">{formatCurrency(unitPrice)}</td>
                                  <td className="py-1.5 px-2 text-right font-medium text-emerald-700">{formatCurrency(finalItemValue)}</td>
                                  <td className="py-1.5 px-2 text-center text-slate-600">{item.quantity}</td>
                                  <td className="py-1.5 px-2 text-center">
                                    {selected > 0 ? (
                                      <input
                                        type="number"
                                        min={1}
                                        max={item.quantity}
                                        value={selected}
                                        onChange={e => {
                                          const val = Math.min(Math.max(1, parseInt(e.target.value) || 1), item.quantity);
                                          setSelectedReplacements(prev => ({ ...prev, [key]: val }));
                                        }}
                                        className="w-14 text-center border border-slate-300 rounded px-1 py-0.5 text-xs focus:ring-1 focus:ring-emerald-500"
                                      />
                                    ) : (
                                      <span className="text-slate-300">—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <span className="text-xs text-slate-500">
            {Object.values(selectedReplacements).filter(v => v > 0).length} item(ns) selecionado(s)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setReplacementModalOpen(false)}
              className="px-3 py-1.5 text-xs border border-slate-300 text-slate-600 rounded hover:bg-white"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={replacementSaving || Object.values(selectedReplacements).filter(v => v > 0).length === 0}
              onClick={async () => {
                setReplacementSaving(true);
                setReplacementError(null);
                try {
                  // Build payloads with calculated original_value
                  const payloads: ReplacementOrderItemPayload[] = Object.entries(selectedReplacements)
                    .filter(([, qty]) => qty > 0)
                    .map(([key, qty]) => {
                      const [origOrderId, sku] = key.split(':');
                      // Find the item in expanded order items to calculate original_value
                      const item = expandedOrderItems.find(i => i.sku === sku);
                      let originalValue: number | undefined;
                      if (item) {
                        const unitPrice = item.unit_price || 0;
                        const itemDiscount = item.discount || 0;
                        const priceAfterItemDiscount = unitPrice * (1 - itemDiscount / 100);
                        const orderDiscount = expandedOrderData?.order_discount ?? 0;
                        const orderProductsTotal = expandedOrderData?.order_products_total ?? 0;
                        const globalDiscountPercent = orderProductsTotal > 0 && orderDiscount > 0
                          ? orderDiscount / orderProductsTotal
                          : 0;
                        originalValue = Math.round(priceAfterItemDiscount * (1 - globalDiscountPercent) * 10000) / 10000;
                      }
                      return { original_order_id: parseInt(origOrderId), sku, quantity: qty, original_value: originalValue };
                    });
                  const created = await salesOrderService.addReplacementItems(order.order_id, payloads);
                  setReplacementItems(prev => [...prev, ...created]);
                  setSelectedReplacements({});
                  setReplacementModalOpen(false);

                  // Recalculate product_replacement_discount_value
                  // Sum of all replacement items (existing + newly created)
                  const allReplacements = [...replacementItems, ...created];
                  const totalReplacementDiscount = allReplacements.reduce(
                    (sum, ri) => sum + (ri.original_value ?? 0) * ri.quantity, 0
                  );
                  // Update order with new product_replacement_discount_value
                  try {
                    const updated = await salesOrderService.updateOrder(order.order_id, {
                      product_replacement_discount_value: Math.round(totalReplacementDiscount * 10000) / 10000,
                    }, order);
                    setOrder(updated);
                  } catch { /* recalc will happen server-side */ }
                } catch (err) {
                  setReplacementError(err instanceof Error ? err.message : 'Erro ao salvar itens de troca');
                } finally { setReplacementSaving(false); }
              }}
              className="px-4 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 flex items-center gap-1.5"
            >
              {replacementSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
              {replacementSaving ? 'Salvando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Aba de histórico/auditoria — admin/finance (orders.view_history). Carrega sob demanda.
  const historyTab: TabItem = {
    id: 'history',
    label: 'Histórico',
    icon: <History size={16} />,
    content: <OrderHistoryTab orderId={order.order_id} />,
  };

  // Build tabs array — cost and installments only for privileged roles
  const tabs: TabItem[] = [itemsTab, volumesTab, replacementTab];
  if (canViewFinancials) {
    tabs.push(costTab, installmentsTab);
  }
  if (canViewHistory) {
    tabs.push(historyTab);
  }

  // Campos de transportadora do card "Frete e Envio" (sem repetir informação).
  const freight = freightCarrierDisplay(order);

  return (
    <div className="space-y-6 max-w-6xl">
      <ApiErrorModal error={actionError} onClose={() => setActionError(null)} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => runWithUnsavedVolumeGuard(() => navigate('/sales-orders'))}><ArrowLeft size={20} /></Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900">
                Pedido #{order.order_id}
              </h1>
              {getStatusBadge(order.order_status_id)}
              {order.stock_updated === true ? (
                order.order_status_id === OrderStatusId.Cancelado ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Estoque estornado</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Estoque lançado</span>
                )
              ) : (
                order.order_status_id !== OrderStatusId.Cancelado && allStatuses.find(s => s.order_status_id === order.order_status_id)?.allocate_available_stock && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Reservando estoque</span>
                )
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              ERP: {order.order_num_erp}
              {order.order_num_ecommerce && ` | Loja/CRM: ${order.order_num_ecommerce}`}
              {order.proposal_num && ` | Proposta: ${order.proposal_num}`}
              {' | '}Criado em {formatDateTime(order.created_at)}
            </p>
            {!!order.invoice_id && (
              <p className="text-sm mt-1">
                <Link
                  to={`/invoices/${order.invoice_id}`}
                  onClick={handleGuardedInternalLinkClick}
                  className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800 hover:underline font-medium"
                >
                  <FileText size={14} />
                  Nota Fiscal #{order.invoice_id}
                </Link>
              </p>
            )}
            {!!order.gift_invoice_id && (
              <p className="text-sm mt-1">
                <Link
                  to={`/invoices/${order.gift_invoice_id}`}
                  onClick={handleGuardedInternalLinkClick}
                  className="inline-flex items-center gap-1 text-pink-600 hover:text-pink-800 hover:underline font-medium"
                >
                  <FileText size={14} />
                  Nota Fiscal Presente #{order.gift_invoice_id}
                </Link>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon={<FileText size={18} />} loading={pdfLoading} onClick={handleDownloadPdf}>
            Imprimir PDF
          </Button>
          <PermissionGuard permission="orders.read">
            <Button variant="outline" icon={<Tag size={18} />} loading={labelLoading} onClick={handleDownloadShippingLabel}>
              Imprimir Etiqueta
            </Button>
          </PermissionGuard>
          {/* Checkout — respects BlockOrderChange and invoice-based readonly.
              Desabilitado quando o estoque já foi lançado: reconferir tentaria relançar o estoque
              e o backend rejeita ("estoque já foi lançado para este pedido"). Estorne o estoque
              antes de reconferir. */}
          {!isReadonly
            && order.order_status_id !== OrderStatusId.Cancelado
            && order.order_status_id !== OrderStatusId.Conferido && (
            <PermissionGuard permission="orders.change_status">
              <Button
                variant="secondary"
                icon={<ClipboardCheck size={18} />}
                onClick={() => runWithUnsavedVolumeGuard(handleStartCheckout)}
                disabled={!!order.stock_updated || (addressReviewBlocked && order.order_status_id !== OrderStatusId.EmConferencia)}
                title={order.stock_updated
                  ? 'Checkout desabilitado porque o estoque já foi lançado. Estorne o estoque para reconferir.'
                  : addressReviewBlocked && order.order_status_id !== OrderStatusId.EmConferencia
                    ? addressReviewBlockMsg
                    : undefined}
              >
                Checkout
              </Button>
            </PermissionGuard>
          )}
          {/* Análise de endereço (antifraude) — fachada no Street View, obrigatória na 1ª compra. */}
          {showAddressReviewBtn && (() => {
            const st = addressReview?.review?.status;
            const tone = !addressReview?.review
              ? 'outline'
              : st === AddressReviewStatus.Approved && !addressReviewBlocked
                ? 'outline'
                : 'danger';
            const label = !addressReview?.review
              ? 'Analisar endereço'
              : `Análise de endereço: ${addressReviewBlocked && st === AddressReviewStatus.Approved ? 'refazer' : addressReviewStatusLabel(st).toLowerCase()}`;
            return (
              <Button
                variant={tone as 'outline' | 'danger'}
                icon={addressReviewBlocked ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
                onClick={() => setAddressReviewOpen(true)}
                title={addressReviewBlocked ? addressReviewBlockMsg : undefined}
              >
                {label}
              </Button>
            );
          })()}
          <PermissionGuard permission="orders.clone">
            <Button variant="outline" icon={<Copy size={18} />} onClick={() => runWithUnsavedVolumeGuard(() => setCloneOpen(true))}>
              Clonar
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="orders.split">
            <Button
              variant="outline"
              icon={<Scissors size={18} />}
              onClick={() => runWithUnsavedVolumeGuard(() => setSplitOpen(true))}
              disabled={isStatusBlocked || !!order.stock_updated}
              title={order.stock_updated ? 'Pedido com estoque já lançado não pode ser desmembrado. Estorne o estoque antes de desmembrar.' : undefined}
            >
              Desmembrar
            </Button>
          </PermissionGuard>
          <PermissionGuard permission="orders.reverse_stock">
            <Button
              variant="outline"
              icon={<Undo2 size={18} />}
              onClick={() => runWithUnsavedVolumeGuard(() => setReverseStockOpen(true))}
              disabled={!order.stock_updated || !!order.invoice_id || !!order.gift_invoice_id}
              title={
                (order.invoice_id || order.gift_invoice_id)
                  ? 'Não é possível estornar: o pedido possui nota fiscal vinculada.'
                  : !order.stock_updated
                    ? 'Não há lançamento de estoque para estornar neste pedido.'
                    : undefined
              }
            >
              Estornar Estoque
            </Button>
          </PermissionGuard>
          {/* Depois de um estorno aprovado, o botão vira o comprovante (para todos que
              veem o pedido); antes, só o admin estorna. */}
          {(() => {
            const approved = latestApprovedRefund(cardRefunds);
            if (approved) {
              return (
                <Button
                  variant="outline"
                  icon={<FileText size={18} />}
                  loading={refundPdfLoading}
                  onClick={() => handleDownloadRefundReceipt(approved.credit_card_refund_id)}
                  title="Baixar o comprovante de cancelamento do cartão (PDF) do estorno mais recente."
                >
                  Baixar comprovante de estorno
                </Button>
              );
            }
            return hasRole('admin') && hasHubPaymentRef(order) ? (
              <Button
                variant="outline"
                icon={<CreditCard size={18} />}
                onClick={() => setCardRefundOpen(true)}
                title="Estornar (total ou parcialmente) a cobrança de cartão de crédito do pedido na Cielo, pelo PaymentHub."
              >
                Estornar cartão
              </Button>
            ) : null;
          })()}
          {/* Nota fiscal não é pré-requisito: existe entrega legítima sem NF emitida. */}
          {canMarkDelivered &&
            [OrderStatusId.Conferido, OrderStatusId.Atendido, OrderStatusId.Enviado].includes(
              order.order_status_id ?? 0,
            ) && (
              <Button
                variant="success"
                icon={<CheckCircle size={18} />}
                onClick={() => runWithUnsavedVolumeGuard(() => setMarkDeliveredOpen(true))}
                title="Marcar o pedido como entregue, informando a data/hora e o motivo (anexado à observação interna)."
              >
                Marcar como entregue
              </Button>
            )}
          {(() => {
            // Seção de emissão de NF: aparece quando o pedido ainda tem NF pendente
            // e o status permite (Conferido, Atendido, ou qualquer status onde já exista
            // uma das NFs emitidas mas falte a outra).
            // Também bloqueia se o tipo de pedido não gera nota fiscal (GenerateInvoice = false).
            const currentOrderType = orderTypes.find(t => t.order_type_id === order.order_type_id);
            const orderTypeBlocksInvoice = currentOrderType && !currentOrderType.generate_invoice;
            const isOrderTypeFullGift = Boolean(
              currentOrderType?.generate_invoice && !currentOrderType?.has_financial_value,
            );
            const canShowEmitSection = !orderTypeBlocksInvoice && (
              [OrderStatusId.Conferido, OrderStatusId.Atendido].includes(order.order_status_id ?? 0)
              || (!!order.invoice_id && !order.gift_invoice_id)
              || (!!order.gift_invoice_id && !order.invoice_id)
            ) && order.order_status_id !== OrderStatusId.Cancelado;
            if (!canShowEmitSection) {
              if (orderTypeBlocksInvoice) {
                return (
                  <div className="relative group">
                    <Button variant="outline" icon={<Send size={18} />} disabled>
                      Emitir NF
                    </Button>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Este tipo de pedido ({currentOrderType?.order_type}) não permite emissão de nota fiscal
                    </div>
                  </div>
                );
              }
              return null;
            }
            return (
            <PermissionGuard permission="invoices.create">
              {(() => {
                const hasVolumes = volumes.length > 0;
                const hasSelectedFreight = freightQuotes.some(q => q.is_selected);
                const canEmitInvoice = hasVolumes && hasSelectedFreight;
                const tooltipMsg = !hasVolumes && !hasSelectedFreight
                  ? 'Cadastre os volumes e selecione a transportadora antes de emitir a nota'
                  : !hasVolumes
                  ? 'Cadastre os volumes do pedido antes de emitir a nota'
                  : !hasSelectedFreight
                  ? 'Selecione a transportadora antes de emitir a nota'
                  : '';

                // A NF de venda exige, além disso, que o pedido de venda esteja pago — o backend
                // rejeita a emissão. A NF de bonificação não é cobrança e segue liberada.
                // E, no pedido de 1ª compra, a análise de endereço (antifraude) aprovada.
                const canEmitSalesInvoice = canEmitInvoice && !unpaidSaleBlocked && !addressReviewBlocked;
                const salesTooltipMsg = unpaidSaleBlocked
                  ? UNPAID_SALE_INVOICE_BLOCK_MSG
                  : addressReviewBlocked
                    ? addressReviewBlockMsg
                    : tooltipMsg;

                const orderItems = order.items ?? [];
                // Robust detection: use has_gift_items from backend, or infer from items
                const hasGiftItems = isOrderTypeFullGift
                  || order.has_gift_items === true
                  || orderItems.some(i => i.is_gift === true)
                  || orderItems.some(i => i.is_gift == null && (!i.unit_price || i.unit_price === 0));
                const hasNonGiftItems = !isOrderTypeFullGift && (
                  orderItems.some(i => i.is_gift === false)
                  || orderItems.some(i => i.is_gift == null && (i.unit_price != null && i.unit_price > 0))
                  || orderItems.some(i => i.is_gift !== true && i.unit_price != null && i.unit_price > 0)
                );
                // Botão de NF de venda só aparece se houver itens NÃO presente
                // e a NF de venda ainda não foi emitida.
                const showSalesInvoiceBtn = !order.invoice_id && hasNonGiftItems;
                // Botão de NF de bonificação só aparece se houver itens presente
                // e a NF de bonificação ainda não foi emitida — e NUNCA quando o
                // brinde já viaja na nota de venda, que é o caso do presente de
                // promoção do site (uma nota só, brinde a valor cheio + desconto).
                const showGiftInvoiceBtn = !order.gift_invoice_id && hasGiftItems
                  && !giftRidesSalesInvoice(order, isOrderTypeFullGift);

                return (
                  <>
                    {showSalesInvoiceBtn && (
                      <div className="relative group">
                        <Button
                          variant="outline"
                          icon={<Send size={18} />}
                          onClick={() => runWithUnsavedVolumeGuard(() => navigate('/invoices/new', { state: { orderData: order, invoiceMode: 'sales' } }))}
                          disabled={!canEmitSalesInvoice}
                        >
                          Emitir NF Venda
                        </Button>
                        {!canEmitSalesInvoice && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg max-w-xs text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                            {salesTooltipMsg}
                          </div>
                        )}
                      </div>
                    )}
                    {showGiftInvoiceBtn && (
                      <div className="relative group">
                        <Button
                          variant="outline"
                          icon={<Send size={18} />}
                          onClick={() => runWithUnsavedVolumeGuard(() => navigate('/invoices/new', { state: { orderData: order, invoiceMode: 'gift', forceFullGiftOrder: isOrderTypeFullGift } }))}
                          disabled={!canEmitInvoice}
                          className="!border-pink-300 !text-pink-700 hover:!bg-pink-50"
                        >
                          Emitir NF Bonificação
                        </Button>
                        {!canEmitInvoice && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                            {tooltipMsg}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </PermissionGuard>
            );
          })()}
        </div>
      </div>

      {pdfError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{pdfError}</div>
      )}

      {/* Order blocked after invoice */}
      {!!order.invoice_id && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm flex items-center gap-2">
          <FileText size={16} className="text-purple-600" />
          <span className="text-purple-700">
            Este pedido está <span className="font-semibold">bloqueado para edição</span> pois possui nota fiscal emitida.
            {order.invoice_id && (
              <>
                {' '}
                <Link to={`/invoices/${order.invoice_id}`} onClick={handleGuardedInternalLinkClick} className="font-semibold underline hover:text-purple-900">
                  Ver Nota Fiscal #{order.invoice_id}
                </Link>
              </>
            )}
            {order.gift_invoice_id && (
              <>
                {' | '}
                <Link to={`/invoices/${order.gift_invoice_id}`} onClick={handleGuardedInternalLinkClick} className="font-semibold underline hover:text-pink-900 text-pink-700">
                  Ver NF Presente #{order.gift_invoice_id}
                </Link>
              </>
            )}
          </span>
        </div>
      )}

      {/* Parent/child references */}
      {order.parent_order_id && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm flex items-center gap-2">
          <ExternalLink size={16} className="text-indigo-600" />
          <span className="text-indigo-700">
            Este pedido é parte do pedido original{' '}
            <Link to={`/sales-orders/${order.parent_order_id}`} onClick={handleGuardedInternalLinkClick} className="font-semibold underline hover:text-indigo-900">
              #{order.parent_order_id}
            </Link>
          </span>
        </div>
      )}
      {order.child_orders && order.child_orders.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex items-center gap-2">
            <Scissors size={16} className="text-indigo-600 shrink-0" />
            <span className="text-indigo-700">
              Pedido desmembrado em:{' '}
              {order.child_orders.map((c, i) => (
                <React.Fragment key={c.order_id}>
                  {i > 0 && ', '}
                  <Link to={`/sales-orders/${c.order_id}`} onClick={handleGuardedInternalLinkClick} className="font-semibold underline hover:text-indigo-900">
                    #{c.order_id}
                  </Link>
                  {c.order_status && <span className="text-indigo-500"> ({c.order_status})</span>}
                </React.Fragment>
              ))}
            </span>
          </div>
          {canChangeStatusOfDesmembradoParent && (
            <p className="flex items-start gap-2 text-indigo-700">
              <Info size={15} className="mt-0.5 shrink-0 text-indigo-600" />
              <span>
                Todos os pedidos filhos estão cancelados, então este pedido voltou a valer e o status
                dele pode ser alterado. Os itens seguem bloqueados enquanto o status for "Desmembrado".
              </span>
            </p>
          )}
        </div>
      )}

      {/* Empresa + Pedido */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader title="Empresa" />
          <CardContent>
            <dl className="space-y-2">
              <Field label="Nome da Empresa"><span className="font-medium">{order.company_name || '—'}</span></Field>
              <Field label="CNPJ"><span>{formatDocument(order.company_document)}</span></Field>
              <Field label="ID Empresa"><span>{order.company_id ?? '—'}</span></Field>
            </dl>
            {/* A empresa emite a NF-e, então só muda antes dela — e num pedido não cancelado. */}
            {canChangeCompany && (
              <PermissionGuard permission="orders.change_company">
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => { setCompanyError(null); setCompanyModalOpen(true); }}
                >
                  Alterar empresa
                </Button>
              </PermissionGuard>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Pedido" />
          <CardContent>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-2">
              <Field label="Status do Pedido">
                {isStatusFieldReadonly ? (
                  getStatusBadge(order.order_status_id)
                ) : (
                  <PermissionGuard permission="orders.change_status" fallback={getStatusBadge(order.order_status_id)}>
                    <div className="space-y-1">
                      <select
                        className="text-sm border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        value={order.order_status_id ?? ''}
                        onChange={e => runWithUnsavedVolumeGuard(() => setStatusChange(Number(e.target.value)))}
                      >
                        {!manualStatusOpts.some(s => s.id === order.order_status_id) && order.order_status_id != null && (
                          <option value={order.order_status_id} disabled>
                            {getStatusOptionById(order.order_status_id)?.label ?? `Status #${order.order_status_id}`}
                          </option>
                        )}
                        {manualStatusOpts
                          .filter(
                            s =>
                              !statusChoicesLimitedToCancel ||
                              s.id === OrderStatusId.Cancelado ||
                              s.id === order.order_status_id,
                          )
                          .map(s => {
                            const blocked = unpaidSaleBlocked && UNPAID_SALE_BLOCKED_STATUS_IDS.includes(s.id);
                            return (
                              <option key={s.id} value={s.id} disabled={blocked}>
                                {blocked ? `${s.label} (requer pagamento)` : s.label}
                              </option>
                            );
                          })}
                      </select>
                      {statusChoicesLimitedToCancel && (
                        <p className="flex items-start gap-1 text-xs text-purple-700">
                          <RotateCcw size={13} className="mt-0.5 shrink-0" />
                          <span>
                            A nota fiscal {order.invoice_number ? `#${order.invoice_number}` : `#${order.invoice_id}`}{' '}
                            deste pedido possui nota de devolução emitida, então o cancelamento está liberado. A NF
                            continua vinculada ao pedido.
                          </span>
                        </p>
                      )}
                      {unpaidSaleBlocked && (
                        <p className="flex items-start gap-1 text-xs text-amber-700">
                          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                          <span>{UNPAID_SALE_STATUS_BLOCK_MSG}</span>
                        </p>
                      )}
                    </div>
                  </PermissionGuard>
                )}
              </Field>
              <Field label="Forma de Pagamento">{order.payment_method_description || '—'}</Field>
              <Field label="Pago">
                {paymentEditing && canMarkOrderAsPaid ? (
                  <div className="space-y-2">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={paymentTempValue}
                        onChange={e => {
                          const checked = e.target.checked;
                          setPaymentTempValue(checked);
                          if (!checked) setPaymentDateTempValue('');
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      Pedido pago
                    </label>
                    <input
                      type="datetime-local"
                      disabled={!paymentTempValue}
                      className="w-full sm:w-64 input-field px-2 py-1.5 text-sm disabled:bg-slate-50"
                      value={paymentDateTempValue}
                      onChange={e => setPaymentDateTempValue(e.target.value)}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={paymentSaving}
                        onClick={async () => {
                          setPaymentSaving(true);
                          setPaymentError(null);
                          try {
                            const updated = await salesOrderService.updateOrder(order.order_id, {
                              is_payed: paymentTempValue,
                              payment_date: paymentTempValue && paymentDateTempValue
                                ? new Date(paymentDateTempValue).toISOString()
                                : null,
                            }, order, { includePaymentFields: canMarkOrderAsPaid });
                            setOrder(prev => prev ? {
                              ...prev,
                              is_payed: updated.is_payed,
                              payment_date: updated.payment_date,
                            } : prev);
                            setPaymentEditing(false);
                          } catch (err) {
                            setPaymentError(err instanceof Error ? err.message : 'Erro ao atualizar pagamento');
                          } finally {
                            setPaymentSaving(false);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                      >
                        {paymentSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        {paymentSaving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        type="button"
                        disabled={paymentSaving}
                        onClick={() => {
                          setPaymentEditing(false);
                          setPaymentError(null);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <X size={12} /> Cancelar
                      </button>
                    </div>
                    {paymentError && <p className="text-xs text-red-500">{paymentError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.is_payed ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {order.is_payed ? 'Sim' : 'Não'}
                    </span>
                    {canMarkOrderAsPaid && (
                      <button
                        type="button"
                        onClick={() => {
                          setPaymentTempValue(Boolean(order.is_payed));
                          setPaymentDateTempValue(toDateTimeLocalValue(order.payment_date));
                          setPaymentError(null);
                          setPaymentEditing(true);
                        }}
                        className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                        title="Editar pagamento"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Data do Pagamento">{order.payment_date ? formatDateTime(order.payment_date) : '—'}</Field>
              <PixReceiptCard orderNumEcommerce={order.order_num_ecommerce} proposalNum={order.proposal_num} />
              <CardRefundsCard orderId={order.order_id} refunds={cardRefunds} />
              <Field label="Tipo de Pedido">
                {(() => {
                  const selectedOrderType = orderTypes.find(t => t.order_type_id === order.order_type_id);
                  const isSelectedTypeFullGift = Boolean(
                    selectedOrderType?.generate_invoice && !selectedOrderType?.has_financial_value,
                  );
                  return (
                    <div className="space-y-1">
                {!isReadonly && canWrite ? (
                  <select
                    className="border border-slate-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={order.order_type_id ?? ''}
                    disabled={orderTypeSaving}
                    onChange={async (e) => {
                      const newVal = e.target.value ? Number(e.target.value) : null;
                      try {
                        setOrderTypeSaving(true);
                        console.log('🏷️ updateOrderType: order state before=', { order_type_id: order.order_type_id, ship_addr_street: order.ship_addr_street, ship_addr_city: order.ship_addr_city, observation: order.observation });
                        const updated = await salesOrderService.updateOrder(order.order_id, { order_type_id: newVal }, order);
                        console.log('🏷️ updateOrderType: response=', { order_type_id: updated.order_type_id, ship_addr_street: updated.ship_addr_street, ship_addr_city: updated.ship_addr_city, observation: updated.observation });
                        setOrder(prev => prev ? { ...prev, order_type_id: updated.order_type_id } : prev);
                      } catch (err) {
                        setActionError(err ?? 'Erro ao atualizar tipo de pedido');
                      } finally {
                        setOrderTypeSaving(false);
                      }
                    }}
                  >
                    <option value="">Selecione...</option>
                    {orderTypes.map(t => (
                      <option key={t.order_type_id} value={t.order_type_id}>{t.order_type}</option>
                    ))}
                  </select>
                ) : (
                  <span>{selectedOrderType?.order_type || '—'}</span>
                )}
                      {isSelectedTypeFullGift && (
                        <p className="text-xs text-pink-700">
                          Pedido presente integral: emitir apenas NF de bonificação (preenche GiftInvoiceId).
                        </p>
                      )}
                    </div>
                  );
                })()}
              </Field>
              <Field label="Canal de Venda">
                {canMarkOrderAsPaid ? (
                  <select
                    className="border border-slate-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={order.sales_channel_id ?? ''}
                    disabled={salesChannelSaving}
                    onChange={async (e) => {
                      const newVal = e.target.value ? Number(e.target.value) : null;
                      try {
                        setSalesChannelSaving(true);
                        const updated = await salesOrderService.updateOrder(order.order_id, { sales_channel_id: newVal }, order);
                        setOrder(prev => prev ? { ...prev, sales_channel_id: updated.sales_channel_id } : prev);
                      } catch (err) {
                        setActionError(err ?? 'Erro ao atualizar canal de venda');
                      } finally {
                        setSalesChannelSaving(false);
                      }
                    }}
                  >
                    <option value="">Selecione...</option>
                    {salesChannels.map(c => (
                      <option key={c.sales_channel_id} value={c.sales_channel_id}>{c.sales_channel_name}</option>
                    ))}
                  </select>
                ) : (
                  <span>{salesChannels.find(c => c.sales_channel_id === order.sales_channel_id)?.sales_channel_name || '—'}</span>
                )}
              </Field>
              <Field label="Estoque Lançado">{order.stock_updated_timestamp ? formatDateTime(order.stock_updated_timestamp) : '—'}</Field>
              <Field label="Primeira Compra">
                {checkPermission('*').hasPermission ? (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={order.is_first_purchase ?? false}
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        try {
                          const updated = await salesOrderService.updateOrder(order.order_id, { is_first_purchase: newVal }, order);
                          setOrder(prev => prev ? { ...prev, is_first_purchase: updated.is_first_purchase } : prev);
                        } catch (err) {
                          setActionError(err ?? 'Erro ao atualizar primeira compra');
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.is_first_purchase ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
                      {order.is_first_purchase ? 'Sim' : 'Não'}
                    </span>
                  </label>
                ) : (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.is_first_purchase ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
                    {order.is_first_purchase ? 'Sim' : 'Não'}
                  </span>
                )}
              </Field>
              <Field label="Estoque Atualizado">
                {order.stock_updated === true ? (
                  order.order_status_id === OrderStatusId.Cancelado ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Estoque estornado</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Estoque lançado</span>
                  )
                ) : (
                  order.order_status_id === OrderStatusId.Cancelado ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">—</span>
                  ) : allStatuses.find(s => s.order_status_id === order.order_status_id)?.allocate_available_stock ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Reservando estoque</span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">—</span>
                  )
                )}
              </Field>
              <Field label="Desconto do Pedido">
                {discountEditing && canEditDiscount ? (
                  <div className="space-y-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full sm:w-48 input-field px-2 py-1.5 text-sm"
                      value={discountTempValue}
                      onChange={(e) => setDiscountTempValue(parseFloat(e.target.value) || 0)}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={discountSaving}
                        onClick={async () => {
                          setDiscountError(null);
                          if (discountTempValue < 0) {
                            setDiscountError('Valor do desconto não pode ser negativo');
                            return;
                          }
                          await runOrderTotalChange(totalChanged(order.order_discount ?? 0, discountTempValue), async (recalculateInstallments) => {
                            setDiscountSaving(true);
                            try {
                              await salesOrderService.updateOrder(order.order_id, {
                                order_discount: discountTempValue,
                              }, order, { recalculateInstallments });
                              // Re-fetch full order to get backend-recalculated totals (considers returned quantities)
                              const refreshed = await salesOrderService.getOrderById(order.order_id);
                              setOrder(refreshed);
                              setDiscountEditing(false);
                            } catch (err) {
                              setDiscountError(err instanceof Error ? err.message : 'Erro ao salvar desconto');
                            } finally {
                              setDiscountSaving(false);
                            }
                          });
                        }}
                        className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                      >
                        {discountSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        {discountSaving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        type="button"
                        disabled={discountSaving}
                        onClick={() => { setDiscountEditing(false); setDiscountError(null); }}
                        className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <X size={12} /> Cancelar
                      </button>
                    </div>
                    {discountError && <p className="text-xs text-red-500">{discountError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span>{formatCurrency(order.order_discount)}</span>
                    {canEditDiscount && !isReadonly && (
                      <button
                        type="button"
                        onClick={() => {
                          setDiscountTempValue(order.order_discount ?? 0);
                          setDiscountEditing(true);
                          setDiscountError(null);
                        }}
                        className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                        title="Editar desconto"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Outras Despesas">
                {otherExpensesEditing && canEditOtherExpenses ? (
                  <div className="space-y-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full sm:w-48 input-field px-2 py-1.5 text-sm"
                      value={otherExpensesTempValue}
                      onChange={(e) => setOtherExpensesTempValue(parseFloat(e.target.value) || 0)}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={otherExpensesSaving}
                        onClick={async () => {
                          setOtherExpensesError(null);
                          if (otherExpensesTempValue < 0) {
                            setOtherExpensesError('Valor não pode ser negativo');
                            return;
                          }
                          await runOrderTotalChange(totalChanged(order.other_expenses ?? 0, otherExpensesTempValue), async (recalculateInstallments) => {
                            setOtherExpensesSaving(true);
                            try {
                              await salesOrderService.updateOrder(order.order_id, {
                                other_expenses: otherExpensesTempValue,
                              }, order, { recalculateInstallments });
                              // Re-fetch full order to get backend-recalculated totals (considers returned quantities)
                              const refreshed = await salesOrderService.getOrderById(order.order_id);
                              setOrder(refreshed);
                              setOtherExpensesEditing(false);
                            } catch (err) {
                              setOtherExpensesError(err instanceof Error ? err.message : 'Erro ao salvar outras despesas');
                            } finally {
                              setOtherExpensesSaving(false);
                            }
                          });
                        }}
                        className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                      >
                        {otherExpensesSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        {otherExpensesSaving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        type="button"
                        disabled={otherExpensesSaving}
                        onClick={() => { setOtherExpensesEditing(false); setOtherExpensesError(null); }}
                        className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <X size={12} /> Cancelar
                      </button>
                    </div>
                    {otherExpensesError && <p className="text-xs text-red-500">{otherExpensesError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span>{formatCurrency(order.other_expenses)}</span>
                    {canEditOtherExpenses && !isReadonly && (
                      <button
                        type="button"
                        onClick={() => {
                          setOtherExpensesTempValue(order.other_expenses ?? 0);
                          setOtherExpensesEditing(true);
                          setOtherExpensesError(null);
                        }}
                        className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                        title="Editar outras despesas"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Desc. Troca/Devolução">
                <span className={`${(order.product_replacement_discount_value ?? 0) > 0 ? 'text-purple-600 font-medium' : ''}`}>
                  {formatCurrency(order.product_replacement_discount_value)}
                </span>
              </Field>
            </dl>
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Cliente */}
        <Card>
          <CardHeader
            title="Cliente"
            action={
              canChangeCustomer && !isReadonly
                ? (
                  <button
                    className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                    onClick={() => runWithUnsavedVolumeGuard(() => setCustomerSearchOpen(true))}
                    disabled={customerChangeSaving}
                  >
                    <Search size={12} /> Alterar Cliente
                  </button>
                )
                : undefined
            }
          />
          <CardContent>
            <dl className="space-y-2">
              <Field label="Nome"><span className="font-medium">{order.customer_name || '—'}</span></Field>
              <Field label="Documento"><span>{formatDocument(order.customer_document)}</span></Field>
              <Field label="Vendedor">{order.seller || '—'}</Field>
              <Field label="Follow-up pós-venda">
                {followupEditing && canEditFollowup ? (
                  <div className="space-y-2">
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={followupTempValue}
                        onChange={e => {
                          const checked = e.target.checked;
                          setFollowupTempValue(checked);
                          if (checked && !followupDateTempValue) {
                            setFollowupDateTempValue(toBrazilDateTimeLocal(order.followup_done_timestamp));
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      Follow-up realizado
                    </label>
                    {followupTempValue && (
                      <div>
                        <span className="block text-xs text-slate-500 mb-1">Data/hora do follow-up (Brasília)</span>
                        <input
                          type="datetime-local"
                          className="w-full sm:w-64 input-field px-2 py-1.5 text-sm"
                          value={followupDateTempValue}
                          onChange={e => setFollowupDateTempValue(e.target.value)}
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={followupSaving}
                        onClick={async () => {
                          setFollowupSaving(true);
                          setFollowupError(null);
                          try {
                            const updated = await salesOrderService.updateFollowup(
                              order.order_id,
                              followupTempValue,
                              followupTempValue ? (followupDateTempValue || null) : null,
                            );
                            setOrder(prev => prev ? {
                              ...prev,
                              followup_done: updated.followup_done,
                              followup_done_timestamp: updated.followup_done_timestamp,
                            } : prev);
                            setFollowupEditing(false);
                          } catch (err) {
                            setFollowupError(err instanceof Error ? err.message : 'Erro ao atualizar follow-up');
                          } finally {
                            setFollowupSaving(false);
                          }
                        }}
                        className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                      >
                        {followupSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        {followupSaving ? 'Salvando...' : 'Salvar'}
                      </button>
                      <button
                        type="button"
                        disabled={followupSaving}
                        onClick={() => {
                          setFollowupEditing(false);
                          setFollowupError(null);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                      >
                        <X size={12} /> Cancelar
                      </button>
                    </div>
                    {followupError && <p className="text-xs text-red-500">{followupError}</p>}
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.followup_done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {order.followup_done ? 'Realizado' : 'Pendente'}
                    </span>
                    {canEditFollowup && (
                      <button
                        type="button"
                        onClick={() => {
                          setFollowupTempValue(Boolean(order.followup_done));
                          setFollowupDateTempValue(toBrazilDateTimeLocal(order.followup_done_timestamp));
                          setFollowupError(null);
                          setFollowupEditing(true);
                        }}
                        className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                        title="Editar follow-up"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                  </div>
                )}
              </Field>
              <Field label="Follow-up em">{order.followup_done_timestamp ? formatDateTimeBR(order.followup_done_timestamp) : '—'}</Field>
            </dl>
          </CardContent>
        </Card>

        {/* Endereço de Entrega — Conferido só admin/finance (canOverrideBlockOrderChange); com NF ninguém (ShippingAddressEditDenial no backend). */}
        <Card>
          <CardHeader
            title="Endereço de Entrega"
            action={
              canWrite && !isReadonly && !order.gift_invoice_id && (order.order_status_id !== OrderStatusId.Conferido || canOverrideBlockOrderChange) && !editingAddress
                ? (
                  <button
                    className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1 transition-colors"
                    onClick={startEditAddress}
                  >
                    <Pencil size={12} /> Editar
                  </button>
                )
                : undefined
            }
          />
          <CardContent>
            {editingAddress ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Logradouro</label>
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_street ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_street: e.target.value }))} placeholder="Rua / Av." />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Número</label>
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_num ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_num: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Complemento</label>
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_compl ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_compl: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Bairro</label>
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_district ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_district: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">CEP</label>
                    {/* normalizeCep tira a máscara antes do maxLength cortar: digitar
                        "04037-002" gravava "04037-00" em silêncio. */}
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_zip ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_zip: normalizeCep(e.target.value) }))} maxLength={9} placeholder="00000000" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">Cidade</label>
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_city ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_city: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider">UF</label>
                    <input className="mt-0.5 w-full input-field px-2 py-1.5 text-sm" value={addressForm.ship_addr_state ?? ''} onChange={e => setAddressForm(f => ({ ...f, ship_addr_state: e.target.value.toUpperCase() }))} maxLength={2} placeholder="SP" />
                  </div>
                </div>
                <AddressCepNotice check={addressCepCheck} onApplyCep={applyCepToAddressForm} />
                <div className="flex gap-2 pt-1">
                  <Button size="sm" loading={addressSaving} onClick={() => saveAddress()} icon={<Save size={14} />}>Salvar</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingAddress(false)}>Cancelar</Button>
                </div>
              </div>
            ) : (
              <dl className="space-y-2">
                <Field label="Logradouro">
                  {order.ship_addr_street || '—'}{order.ship_addr_num ? `, ${order.ship_addr_num}` : ''}
                </Field>
                {order.ship_addr_compl && <Field label="Complemento">{order.ship_addr_compl}</Field>}
                <Field label="Bairro">{order.ship_addr_district || '—'}</Field>
                <Field label="Cidade/UF">
                  {order.ship_addr_city || '—'} - {order.ship_addr_state || '—'}
                </Field>
                <Field label="CEP">{order.ship_addr_zip || '—'}</Field>
                {/* Em modo leitura só interessa o problema: "conferindo…" e "serviço fora" ficam ocultos. */}
                {(addressCepCheck.status === 'notFound' || addressCepCheck.divergences.length > 0) && (
                  <AddressCepNotice check={addressCepCheck} />
                )}
              </dl>
            )}
            <ConfirmDialog
              isOpen={cepDivergenceConfirmOpen}
              onClose={() => setCepDivergenceConfirmOpen(false)}
              onConfirm={() => {
                setCepDivergenceConfirmOpen(false);
                void saveAddress(true);
              }}
              title="Endereço não confere com o CEP"
              message="O endereço de entrega não confere com o CEP informado. Salvar mesmo assim?"
              confirmText="Salvar mesmo assim"
              cancelText="Revisar endereço"
              severity="warning"
            />
          </CardContent>
        </Card>

        {/* Frete e Status */}
        <Card>
          <CardHeader
            title="Frete e Envio"
            action={
              canWrite && !isReadonly && !shippingPriceEditing ? (
                <button
                  type="button"
                  onClick={() => {
                    setShippingPriceTempValue(order.shipping_price ?? 0);
                    setShippingPriceEditing(true);
                    setShippingPriceError(null);
                  }}
                  className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                  title="Editar valor do frete"
                >
                  <Pencil size={14} />
                </button>
              ) : undefined
            }
          />
          <CardContent>
            <dl className="space-y-2">
              {/* Escolha do cliente: a transportadora estruturada (pedidos novos do site/CRM) ou,
                  em pedido antigo, o texto do checkout. A opção exibida no checkout só aparece à
                  parte quando acrescenta algo (ex.: frete promocional), e a transportadora de envio
                  só quando é outra — nada se repete. Regra em freightCarrierDisplay. */}
              <Field label="Transportadora escolhida pelo cliente">
                {freight.chosen || '—'}
                {freight.chosenIsLegacyText && (
                  <span
                    className="ml-2 text-xs text-slate-400"
                    title="Pedido anterior ao registro estruturado da transportadora: só o texto da opção exibida no checkout."
                  >
                    (texto do checkout)
                  </span>
                )}
              </Field>
              {freight.checkoutLabel && (
                <Field label="Opção exibida no checkout">{freight.checkoutLabel}</Field>
              )}
              {freight.shippedBy && (
                <Field label="Transportadora de envio">{freight.shippedBy}</Field>
              )}
              {shippingPriceEditing ? (
                <div className="space-y-2">
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Valor do Frete (pago pelo cliente)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full sm:w-48 input-field px-2 py-1.5 text-sm"
                    value={shippingPriceTempValue}
                    onChange={(e) => setShippingPriceTempValue(parseFloat(e.target.value) || 0)}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={shippingPriceSaving}
                      onClick={async () => {
                        setShippingPriceError(null);
                        if (shippingPriceTempValue < 0) {
                          setShippingPriceError('Valor do frete não pode ser negativo');
                          return;
                        }
                        await runOrderTotalChange(totalChanged(order.shipping_price ?? 0, shippingPriceTempValue), async (recalculateInstallments) => {
                          setShippingPriceSaving(true);
                          try {
                            await salesOrderService.updateOrder(order.order_id, {
                              shipping_price: shippingPriceTempValue,
                            }, order, { recalculateInstallments });
                            // Re-fetch full order to get backend-recalculated totals (considers returned quantities)
                            const refreshed = await salesOrderService.getOrderById(order.order_id);
                            setOrder(refreshed);
                            setShippingPriceEditing(false);
                          } catch (err) {
                            setShippingPriceError(err instanceof Error ? err.message : 'Erro ao salvar frete');
                          } finally {
                            setShippingPriceSaving(false);
                          }
                        });
                      }}
                      className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                    >
                      {shippingPriceSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                      {shippingPriceSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      disabled={shippingPriceSaving}
                      onClick={() => { setShippingPriceEditing(false); setShippingPriceError(null); }}
                      className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <X size={12} /> Cancelar
                    </button>
                  </div>
                  {shippingPriceError && <p className="text-xs text-red-500">{shippingPriceError}</p>}
                </div>
              ) : (
                <Field label="Valor do Frete">{formatCurrency(order.shipping_price)}</Field>
              )}
              {/* Freight Discount - only admin/finance can edit */}
              {freightDiscountEditing ? (
                <div className="space-y-2">
                  <label className="text-xs text-slate-500 uppercase tracking-wider font-medium">Desconto no Frete (R$)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full sm:w-48 input-field px-2 py-1.5 text-sm"
                    value={freightDiscountTempValue}
                    onChange={(e) => setFreightDiscountTempValue(parseFloat(e.target.value) || 0)}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={freightDiscountSaving}
                      onClick={async () => {
                        setFreightDiscountError(null);
                        if (freightDiscountTempValue < 0) {
                          setFreightDiscountError('Valor do desconto não pode ser negativo');
                          return;
                        }
                        if (freightDiscountTempValue > (order.shipping_price ?? 0)) {
                          setFreightDiscountError('Desconto não pode ser superior ao valor do frete');
                          return;
                        }
                        await runOrderTotalChange(totalChanged(order.freight_discount_value ?? 0, freightDiscountTempValue), async (recalculateInstallments) => {
                          setFreightDiscountSaving(true);
                          try {
                            await salesOrderService.updateOrder(order.order_id, {
                              freight_discount_value: freightDiscountTempValue,
                            }, order, { recalculateInstallments });
                            const refreshed = await salesOrderService.getOrderById(order.order_id);
                            setOrder(refreshed);
                            setFreightDiscountEditing(false);
                          } catch (err) {
                            setFreightDiscountError(err instanceof Error ? err.message : 'Erro ao salvar desconto do frete');
                          } finally {
                            setFreightDiscountSaving(false);
                          }
                        });
                      }}
                      className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                    >
                      {freightDiscountSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                      {freightDiscountSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      type="button"
                      disabled={freightDiscountSaving}
                      onClick={() => { setFreightDiscountEditing(false); setFreightDiscountError(null); }}
                      className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <X size={12} /> Cancelar
                    </button>
                  </div>
                  {freightDiscountError && <p className="text-xs text-red-500">{freightDiscountError}</p>}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Field label="Desconto no Frete">{formatCurrency(order.freight_discount_value)}</Field>
                  {canEditFreightDiscount && !isReadonly && (
                    <button
                      type="button"
                      onClick={() => {
                        setFreightDiscountTempValue(order.freight_discount_value ?? 0);
                        setFreightDiscountEditing(true);
                        setFreightDiscountError(null);
                      }}
                      className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                      title="Editar desconto do frete"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                </div>
              )}
              <Field label="Prazo entrega">{order.delivery_time_chosen_by_customer ? `${order.delivery_time_chosen_by_customer} dias úteis` : '—'}</Field>
              {order.delivery_deadline && (
                <Field label="Data limite de entrega">
                  <span className="flex items-center gap-2">
                    {formatDate(order.delivery_deadline)}
                    {order.is_delivery_late && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        <AlertTriangle size={12} /> Atrasado
                      </span>
                    )}
                  </span>
                </Field>
              )}
              {order.shipping_timestamp && <Field label="Data de Envio">{formatDate(order.shipping_timestamp)}</Field>}
              {order.delivery_timestamp && <Field label="Data de Entrega">{formatDateTime(order.delivery_timestamp)}</Field>}
              {order.shipping_status && <Field label="Status Envio">{order.shipping_status}</Field>}
              {isTrackingLink(order.shipping_track_url) && (
                <Field label="Rastreamento">
                  <a href={order.shipping_track_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline text-xs flex items-center gap-1">
                    Rastrear <ExternalLink size={12} />
                  </a>
                </Field>
              )}
              {order.order_status_id === OrderStatusId.Entregue && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => runWithUnsavedVolumeGuard(openDeliveryModal)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <CheckCircle size={14} /> Ver detalhes da entrega
                  </button>
                </div>
              )}
              <OrderDeliveryTrackingCard orderId={order.order_id} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Observations — sempre exibido, editável se canWrite */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Observação */}
        <Card>
          <CardHeader
            title="Observação"
            action={
              canWrite && !isReadonly && !obsEditing ? (
                <button
                  type="button"
                  onClick={() => { setObsTempValue(order.observation ?? ''); setObsEditing(true); setObsError(null); }}
                  className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                  title="Editar observação"
                >
                  <Pencil size={14} />
                </button>
              ) : undefined
            }
          />
          <CardContent>
            {obsEditing ? (
              <div className="space-y-2">
                <textarea
                  value={obsTempValue}
                  onChange={e => setObsTempValue(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                  placeholder="Observação visível para o cliente..."
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={obsSaving}
                    onClick={async () => {
                      setObsSaving(true); setObsError(null);
                      try {
                        const updated = await salesOrderService.updateOrder(order.order_id, { observation: obsTempValue || undefined }, order);
                        setOrder(prev => prev ? { ...prev, observation: updated.observation, internal_observation: updated.internal_observation } : prev);
                        setObsEditing(false);
                      } catch (err) {
                        setObsError(err instanceof Error ? err.message : 'Erro ao salvar');
                      } finally { setObsSaving(false); }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                  >
                    {obsSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                    {obsSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    disabled={obsSaving}
                    onClick={() => { setObsEditing(false); setObsError(null); }}
                    className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <X size={12} /> Cancelar
                  </button>
                  {obsError && <span className="text-xs text-red-500">{obsError}</span>}
                </div>
              </div>
            ) : order.observation ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.observation}</p>
            ) : (
              <p className="text-sm text-slate-400 italic">
                {canWrite && !isReadonly ? 'Nenhuma observação. Clique em editar para adicionar.' : 'Nenhuma observação.'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Observação Interna */}
        <Card>
          <CardHeader
            title="Observação Interna"
            action={
              canWrite && !intObsEditing ? (
                <button
                  type="button"
                  onClick={() => { setIntObsTempValue(order.internal_observation ?? ''); setIntObsEditing(true); setIntObsError(null); }}
                  className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                  title="Editar observação interna"
                >
                  <Pencil size={14} />
                </button>
              ) : undefined
            }
          />
          <CardContent>
            {intObsEditing ? (
              <div className="space-y-2">
                <textarea
                  value={intObsTempValue}
                  onChange={e => setIntObsTempValue(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
                  placeholder="Observação interna (não visível para o cliente)..."
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={intObsSaving}
                    onClick={async () => {
                      setIntObsSaving(true); setIntObsError(null);
                      try {
                        const updated = await salesOrderService.updateInternalObservation(order.order_id, intObsTempValue || '');
                        setOrder(prev => prev ? { ...prev, internal_observation: updated.internal_observation } : prev);
                        setIntObsEditing(false);
                      } catch (err) {
                        setIntObsError(err instanceof Error ? err.message : 'Erro ao salvar');
                      } finally { setIntObsSaving(false); }
                    }}
                    className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                  >
                    {intObsSaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                    {intObsSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    disabled={intObsSaving}
                    onClick={() => { setIntObsEditing(false); setIntObsError(null); }}
                    className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <X size={12} /> Cancelar
                  </button>
                  {intObsError && <span className="text-xs text-red-500">{intObsError}</span>}
                </div>
              </div>
            ) : order.internal_observation ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.internal_observation}</p>
            ) : (
              <p className="text-sm text-slate-400 italic">
                {canWrite ? 'Nenhuma observação interna. Clique em editar para adicionar.' : 'Nenhuma observação interna.'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Prioridade e Hora Requisitada de Entrega */}
      <Card>
        <CardHeader
          title="Prioridade e Envio Agendado"
          action={
            canWrite && !isReadonly && !priorityEditing ? (
              <button
                type="button"
                onClick={() => {
                  setPriorityTempValue(order.is_priority ?? false);
                  setPriorityTempTime(toDateTimeLocalValue(order.request_delivery_time));
                  setPriorityTempShipDate(order.scheduled_ship_date ?? '');
                  setPriorityEditing(true);
                  setPriorityError(null);
                }}
                className="p-1 text-slate-400 hover:text-emerald-600 rounded"
                title="Editar prioridade"
              >
                <Pencil size={14} />
              </button>
            ) : undefined
          }
        />
        <CardContent>
          {priorityEditing ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={priorityTempValue}
                  onChange={e => setPriorityTempValue(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm text-slate-700 font-medium">Pedido Prioritário</span>
              </label>
              {priorityTempValue && (
                <div>
                  <label className="text-xs text-slate-500 font-medium">Horário de Entrega Requisitado</label>
                  <input
                    type="datetime-local"
                    className="mt-1 w-full sm:w-auto input-field px-3 py-2 text-sm"
                    value={priorityTempTime}
                    onChange={e => setPriorityTempTime(e.target.value)}
                  />
                  <p className="text-xs text-slate-400 mt-0.5">Opcional — se não informado, apenas a prioridade será marcada</p>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-500 font-medium">Data de Envio Agendado</label>
                <div className="mt-1">
                  <DateInputBR
                    className="border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={priorityTempShipDate}
                    onChange={v => setPriorityTempShipDate(v)}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Com data marcada o checkout segue normal, mas o pedido não entra em rota de
                  entrega nem gera pedido de envio antes desse dia. Limpe o campo para remover.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={prioritySaving}
                  onClick={async () => {
                    setPrioritySaving(true); setPriorityError(null);
                    try {
                      const updated = await salesOrderService.updateOrder(order.order_id, {
                        is_priority: priorityTempValue,
                        request_delivery_time: priorityTempValue && priorityTempTime
                          ? priorityTempTime
                          : null,
                        scheduled_ship_date: priorityTempShipDate || null,
                      }, order);
                      setOrder(prev => prev ? { ...prev, is_priority: updated.is_priority, request_delivery_time: updated.request_delivery_time, scheduled_ship_date: updated.scheduled_ship_date } : prev);
                      setPriorityEditing(false);
                    } catch (err) {
                      setPriorityError(err instanceof Error ? err.message : 'Erro ao salvar');
                    } finally { setPrioritySaving(false); }
                  }}
                  className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-3 py-1.5 disabled:opacity-50"
                >
                  {prioritySaving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                  {prioritySaving ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  disabled={prioritySaving}
                  onClick={() => { setPriorityEditing(false); setPriorityError(null); }}
                  className="inline-flex items-center gap-1.5 text-xs border border-slate-300 text-slate-600 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                >
                  <X size={12} /> Cancelar
                </button>
                {priorityError && <span className="text-xs text-red-500">{priorityError}</span>}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-4">
              {order.is_priority ? (
                <>
                  <span className="inline-flex items-center gap-1.5 text-xs bg-red-100 text-red-700 rounded-full px-3 py-1 font-semibold">
                    <AlertTriangle size={12} /> PRIORITÁRIO
                  </span>
                  {order.request_delivery_time && (
                    <span className="text-sm text-slate-700">
                      Entrega requisitada: <strong>{new Date(order.request_delivery_time).toLocaleString('pt-BR')}</strong>
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm text-slate-400 italic">
                  {canWrite ? 'Não prioritário. Clique em editar para alterar.' : 'Não prioritário.'}
                </span>
              )}
              {order.scheduled_ship_date && (
                <span className="inline-flex items-center gap-1.5 text-xs bg-blue-100 text-blue-700 rounded-full px-3 py-1 font-semibold" title="Não entra em rota nem gera pedido de envio antes desta data">
                  <Calendar size={12} /> Envio agendado: {isoToBR(order.scheduled_ship_date)}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs tabs={tabs} defaultTab="items" />

      {/* Audit info */}
      <div className="text-xs text-slate-400 flex flex-wrap gap-4">
        <span>Criado em: {formatDateTime(order.created_at)} por {order.created_by || '—'}</span>
        <span>Atualizado em: {formatDateTime(order.updated_at)} por {order.last_updated_by || '—'}</span>
      </div>

      {/* Clone dialog */}
      {replacementModal}
      <ConfirmDialog
        isOpen={cloneOpen}
        onClose={() => setCloneOpen(false)}
        onConfirm={handleClone}
        title="Clonar Pedido"
        message={`Deseja clonar o pedido #${order.order_id}? Um novo pedido será criado com os mesmos dados e status "Em Aberto".`}
        confirmText="Clonar"
        cancelText="Cancelar"
        severity="info"
        loading={cloning}
      />

      {/* Reverse stock (estorno de estoque) dialog */}
      <ConfirmDialog
        isOpen={reverseStockOpen}
        onClose={() => setReverseStockOpen(false)}
        onConfirm={handleReverseStock}
        title="Estornar Estoque"
        message={`Deseja estornar o lançamento de estoque do pedido #${order.order_id}? Os produtos retornarão ao estoque e o pedido voltará a ser tratado como "estoque não lançado". Esta ação não pode ser desfeita automaticamente.`}
        confirmText="Estornar"
        cancelText="Cancelar"
        severity="warning"
        loading={reversingStock}
      />

      {/* Estorno de cartão (somente admin) */}
      {hasRole('admin') && (
        <CardRefundModal
          isOpen={cardRefundOpen}
          orderId={order.order_id}
          onClose={() => setCardRefundOpen(false)}
          onRefunded={() => setCardRefundsRefresh(n => n + 1)}
        />
      )}

      {/* Troca da empresa emissora (só antes da NF) */}
      <ChangeOrderCompanyModal
        isOpen={companyModalOpen}
        currentCompanyId={order.company_id}
        saving={companySaving}
        error={companyError}
        onClose={() => setCompanyModalOpen(false)}
        onConfirm={handleChangeCompany}
      />

      {/* Análise de fraude — endereço (Street View) */}
      <AddressReviewModal
        isOpen={addressReviewOpen}
        orderId={order.order_id}
        orderLabel={order.order_num_ecommerce || order.order_num_erp || order.order_id}
        onClose={() => setAddressReviewOpen(false)}
        onChanged={setAddressReview}
      />

      {/* Marcar como entregue (manual) */}
      <MarkDeliveredModal
        isOpen={markDeliveredOpen}
        orderId={order.order_id}
        orderLabel={order.order_num_ecommerce || order.order_id}
        onClose={() => setMarkDeliveredOpen(false)}
        onConfirmed={(updated) => setOrder(prev => (prev ? { ...prev, ...updated } : updated))}
      />

      {/* Status change dialog */}
      <ConfirmDialog
        isOpen={statusChange !== null}
        onClose={() => setStatusChange(null)}
        onConfirm={confirmStatusChange}
        title="Alterar Status"
        message={`Deseja alterar o status do pedido #${order.order_id} para "${statusChange ? (getStatusOptionById(statusChange)?.label ?? statusChange) : ''}"?`}
        confirmText="Alterar"
        cancelText="Cancelar"
        severity="warning"
        loading={changingStatus}
      />

      {/* Split modal */}
      {splitOpen && order && (
        <SplitOrderModal
          isOpen={true}
          onClose={() => { setSplitOpen(false); setSplitError(null); }}
          onConfirm={handleSplitConfirm}
          order={order}
          loading={splitting}
          error={splitError}
          statusOptions={manualStatusOpts}
        />
      )}

      {/* Volume delete confirm */}
      <ConfirmDialog
        isOpen={volumeDeleteId !== null}
        onClose={() => setVolumeDeleteId(null)}
        onConfirm={confirmDeleteVolume}
        title="Excluir Volume"
        message="Deseja excluir este volume? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
      />

      {/* Unsaved new volume guard */}
      <ConfirmDialog
        isOpen={unsavedVolumeDialogOpen}
        onClose={cancelUnsavedVolumeDialog}
        onConfirm={confirmDiscardUnsavedVolume}
        title="Dados de volume não salvos"
        message="Existe um novo volume com dados preenchidos que ainda não foi salvo. Se continuar, essas informações serão perdidas. Deseja permanecer na tela para salvar?"
        confirmText="Sair sem salvar"
        cancelText="Permanecer na tela"
        severity="warning"
      />

      {/* Item delete confirm */}
      <ConfirmDialog
        isOpen={itemDeleteId !== null}
        onClose={() => setItemDeleteId(null)}
        onConfirm={confirmDeleteItem}
        title="Excluir Item"
        message="Deseja excluir este item do pedido? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
      />

      {/* Installment delete confirm */}
      <ConfirmDialog
        isOpen={installmentDeleteId !== null}
        onClose={() => setInstallmentDeleteId(null)}
        onConfirm={confirmDeleteInstallment}
        title="Excluir Parcela"
        message="Deseja excluir esta parcela? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
      />

      {/* Confirmação do recálculo das parcelas em alteração de valor do pedido.
          "Não" cancela a alteração inteira — nada é gravado —, justamente para não deixar
          o pedido com parcelas que não somam o total, que é o que quebra a emissão da NF. */}
      <ConfirmDialog
        isOpen={installmentRecalcAction !== null}
        onClose={() => setInstallmentRecalcAction(null)}
        onConfirm={confirmInstallmentRecalc}
        title="Recalcular parcelas do pedido"
        message={`Este pedido já possui ${realInstallments.length} ${realInstallments.length === 1 ? 'parcela de pagamento' : 'parcelas de pagamento'} (total atual ${formatCurrency(realInstallments.reduce((s, i) => s + i.value, 0))}). Esta alteração muda o valor total do pedido, e o valor das parcelas será recalculado proporcionalmente para acompanhar o novo total — a quantidade de parcelas, os vencimentos e os meios de pagamento não mudam. Se não confirmar, a alteração não será aplicada. Deseja continuar?`}
        confirmText="Sim, recalcular parcelas"
        cancelText="Não"
        severity="warning"
        loading={installmentRecalcRunning}
      />

      {/* Sem permissão para alterar parcelas: a alteração pararia no backend com 403. */}
      <ConfirmDialog
        isOpen={installmentRecalcBlocked}
        onClose={() => setInstallmentRecalcBlocked(false)}
        onConfirm={() => setInstallmentRecalcBlocked(false)}
        title="Alteração não permitida"
        message="Este pedido já possui parcelas de pagamento e esta alteração muda o valor total do pedido, o que exige recalcular as parcelas. Seu perfil não tem permissão para alterar parcelas — acione o financeiro para fazer esta alteração."
        confirmText="Entendi"
        cancelText="Fechar"
        severity="danger"
      />

      {/* Customer search modal */}
      <CustomerSearchModal
        isOpen={customerSearchOpen}
        onClose={() => setCustomerSearchOpen(false)}
        onSelect={handleCustomerChange}
      />

      {/* Pending orders modal for stock reservations */}
      <PendingOrdersModal
        isOpen={pendingModal.open}
        onClose={() => setPendingModal({ open: false, productId: null, sku: '' })}
        productId={pendingModal.productId}
        productSku={pendingModal.sku}
        productDescription={pendingModal.description}
      />

      {/* Checkout (conferência) modal */}
      {order && (
        <CheckoutModal
          isOpen={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          onComplete={handleCheckoutComplete}
          orderId={order.order_id}
          orderNumErp={order.order_num_erp}
          items={items}
        />
      )}

      {/* Delivery details modal */}
      {deliveryModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Detalhes da Entrega</h3>
              <button type="button" onClick={() => { setDeliveryModalOpen(false); setDeliveryDetails(null); setDeliveryPhotoUrls({}); }} className="p-1 text-slate-400 hover:text-slate-600 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {deliveryLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={24} className="animate-spin text-slate-400" />
                </div>
              ) : deliveryError ? (
                <div className="text-center py-8">
                  <p className="text-red-500 text-sm">{deliveryError}</p>
                  <p className="text-slate-400 text-xs mt-2">O endpoint de detalhes da entrega ainda não está disponível no backend.</p>
                </div>
              ) : deliveryDetails ? (
                <>
                  <dl className="space-y-3">
                    <Field label="Contato que recebeu"><span className="font-medium">{deliveryDetails.delivery_contact}</span></Field>
                    {deliveryDetails.delivery_contact_document && (
                      <Field label="Documento do contato"><span>{deliveryDetails.delivery_contact_document}</span></Field>
                    )}
                    <Field label="Data da entrega">
                      <span>{new Date(deliveryDetails.delivered_at).toLocaleString('pt-BR')}</span>
                    </Field>
                  </dl>
                  {(deliveryPhotoUrls.photo1 || deliveryPhotoUrls.photo2) && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Fotos de evidência</p>
                      <div className="grid grid-cols-2 gap-3">
                        {deliveryPhotoUrls.photo1 && (
                          <img src={deliveryPhotoUrls.photo1} alt="Evidência 1" className="w-full h-48 object-cover rounded-lg border border-slate-200" />
                        )}
                        {deliveryPhotoUrls.photo2 && (
                          <img src={deliveryPhotoUrls.photo2} alt="Evidência 2" className="w-full h-48 object-cover rounded-lg border border-slate-200" />
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-slate-400 py-8">Nenhum detalhe disponível</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
